<template>
  <ThemeOnly theme="light">
    <LightAIChats />
  </ThemeOnly>
  <ThemeOnly theme="modern">
    <ModernAIChats />
  </ThemeOnly>
  <ThemeOnly theme="modern" :invert="true">
    <div ref="aiChatsRoot" class="ai-chats-view-shell">
    <div class="ai-chats-view-header mt-6 ml-4 flex space-between space-x-4 justify-between m-2 mr-4 flex-end">
      <div>
        <h1 class="text-2xl text-gray-900 dark:text-gray-200 font-medium">
          {{ t('aiChats.title', 'AI Chats') }}
        </h1>
        <h1 class="text text-gray-900 dark:text-gray-200">
          {{ t('aiChats.description', 'Creates Generative AI Chats for your needs.') }}
        </h1>
      </div>

      <div class="flex">
        <label class="input input-bordered flex items-center mr-2" style="width:330px">
          <input type="text" v-model="searchValue" inputmode="search" autocomplete="off" spellcheck="false" :maxlength="searchMaxLength" class="grow" :placeholder="t('aiChats.searchPlaceholder', 'Search')" />
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 16 16" fill="currentColor" class="h-4 w-4 opacity-70">
            <path fill-rule="evenodd"
              d="M9.965 11.026a5 5 0 1 1 1.06-1.06l2.755 2.754a.75.75 0 1 1-1.06 1.06l-2.755-2.754ZM10.5 7a3.5 3.5 0 1 1-7 0 3.5 3.5 0 0 1 7 0Z"
              clip-rule="evenodd" />
          </svg>
        </label>
        
        <div class="dropdown dropdown-end mr-4" :class="{ 'dropdown-open': isCreateDropdownOpen === true }" ref="createDropdownRef">
          <button class="btn" @click.stop="toggleCreateDropdown" :disabled="isCreating === true" id="create-new-agent-btn">
            <span v-if="isCreating === true" class="loading loading-spinner loading-sm mr-2"></span>
            {{ t('aiChats.createNewAgentButton', 'Create New Agent') }}
            <Icon icon="heroicons:chevron-down" class="w-4 h-4 ml-2" />
          </button>
          <ul v-if="isCreateDropdownOpen === true" tabindex="0" class="dropdown-content z-[60] menu p-2 shadow bg-base-100 rounded-box w-56">
            <li><button @click="onCreateDropdownSelect('create')">{{ t('aiChats.createNewOption', 'Create New') }}</button></li>
            <li><button @click="onCreateDropdownSelect('import')">{{ t('aiChats.importExistingOption', 'Import Existing') }}</button></li>
            <li v-if="false"><button @click="router.push('/workflows')">{{ t('sidebar.workflowsDropdown', 'Workflows') }}</button></li>
          </ul>
        </div>
      </div>
    </div>
    
    <div v-if="viewMode === 'table'" class="ai-chats-view-body mt-6 px-4">
      <ThemeOnly theme="modern" :invert="true">
        <div class="ai-chats-table-shell overflow-x-auto text-align card shadow bg-white dark:bg-gray-800 w-full rounded-md p-3 border dark:border-gray-700 flex flex-col min-h-0"
          style="border-radius: 10px;">
          <EasyDataTable :headers="translatedHeaders" :items="filteredAiAgents" buttons-pagination :search-field="searchFields"
          :search-value="searchValue" :loading="isLoadingAgents !== false && aiAgents.length === 0">
          
            <template #item-actions="{ actions }">
              <div v-for="action in actions" :key="action.actionType" class="" style="margin-top:2%;margin-bottom: 2%;">
                <span v-if="action.actionType !== undefined && action.actionType !== null && action.actionType.length >= 0" class="text-sm text-gray-500 badge badge-outline" style="margin-bottom:0.1%;">{{ t('aiChats.actionType.' + action.actionType, action.actionType) }}</span>
              </div>
            </template>
            <template #item-agentstatus="{ agentstatus }">
              <div class="flex items-center">
                <div :class="[
                  'agentStatus',
                  agentstatus.toLowerCase() === 'active' ? 'bg-green-500' : 'inactiveAgent'
                ]">
                  {{ agentstatus.toLowerCase() === 'active' ? t('aiChats.statusActive','Active') : t('aiChats.statusDisabled','Disabled') }}
                </div>
              </div>
            </template>
            <template #item-agenttype="{ agenttype }">
              <div class="flex items-center">
                <div :class="[
                  'agentType',
                  agenttype === 'PublicAgent' ? 'bg-blue-500' : 'bg-indigo-500'
                ]">
                  {{ t('aiChats.agentType.' + agenttype, agenttype.replace(/([A-Z])/g, ' $1').trim()) }}
                </div>
              </div>
            </template>
            <template #item-agentname="{ agentname, rowNumber, item }">
              <span>{{ agentname }}</span>
            </template>
            <template #item-channels="{ rowNumber, item }">
              <div class="flex w-full items-center" :style="{ direction: 'ltr', justifyContent: currentLanguage === 'he' || currentLanguage === 'ar' ? 'flex-end' : 'center' }">
                <div v-if="resolveAvailableChannelsForRow(item, rowNumber).length > 0" class="flex w-full flex-wrap items-center" :style="{ direction: 'ltr', justifyContent: currentLanguage === 'he' || currentLanguage === 'ar' ? 'flex-end' : 'center' }">
                  <span v-for="(channel, channelIndex) in resolveAvailableChannelsForRow(item, rowNumber)" :key="channel.id" class="inline-flex h-6 w-6 items-center justify-center rounded-full border border-gray-200 bg-gray-50 text-gray-500 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-300" :style="channelIndex < resolveAvailableChannelsForRow(item, rowNumber).length - 1 ? { marginInlineEnd: '0.5rem' } : {}" :title="channel.label">
                    <Icon :icon="channel.icon" width="12" />
                  </span>
                </div>
              </div>
            </template>
            <template #item-options="{ rowNumber }">
              <button @click="handleOpenChat(rowNumber)" class="mr-4 hover:text-red-900 p-3 inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-indigo-700 bg-indigo-100 hover:bg-indigo-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" :id="'open-chat-btn-' + rowNumber">{{ resolveOpenButtonLabelByRow(rowNumber) }}</button>
              <button @click="handleEditClick(rowNumber)" class="mr-4 hover:text-red-900 p-3 inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-indigo-700 bg-indigo-100 hover:bg-indigo-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" :id="'edit-agent-btn-' + rowNumber">
                <span v-if="isEditing === rowNumber" class="loading loading-spinner loading-sm mr-2"></span>
                {{ t('aiChats.editButton', 'Edit') }}
              </button>
              <button @click="openDeployModal(rowNumber)" class="mr-4 hover:text-red-900 p-3 inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-blue-700 bg-blue-100 hover:bg-blue-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500" :id="'deploy-agent-btn-' + rowNumber">{{ t('aiChats.deployButton', 'Deploy') }}</button>
              <button @click="openKnowledgeStatusModal(rowNumber)" class="mr-4 rtl:ml-4 hover:text-blue-900 inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-blue-700 bg-blue-100 hover:bg-blue-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500" :title="t('aiChats.viewKnowledgeStatusTitle', 'View Knowledge Base Status')" :id="'status-btn-' + rowNumber">
                <span>{{ t('aiChats.statusButton', 'Status') }}</span>
              </button>
              <button @click="handleDeleteClick(rowNumber)" class="hover:text-red-900 p-3 inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-indigo-700 bg-indigo-100 hover:bg-indigo-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" :id="'delete-agent-btn-' + rowNumber">{{ t('aiChats.deleteButton', 'Delete') }}</button>
            </template>
            <template #loading>
              <ModernLoader />
            </template>
            <template #header-agentname>
              <div @click="toggleFilterColumn('agentname')" class="flex flex-col cursor-pointer select-none">
                <span>{{ t('aiChats.table.headerAgentName','Agent Name') }}</span>
                <input v-if="activeFilterColumn === 'agentname' || columnFilters.agentname !== ''" v-model="columnFilters.agentname" :maxlength="columnFilterMaxLength" class="input input-xs mt-1" @click.stop />
              </div>
            </template>
            <template #header-agentstatus>
              <div @click="toggleFilterColumn('agentstatus')" class="flex flex-col cursor-pointer select-none">
                <span>{{ t('aiChats.table.headerStatus','Status') }}</span>
                <input v-if="activeFilterColumn === 'agentstatus' || columnFilters.agentstatus !== ''" v-model="columnFilters.agentstatus" :maxlength="columnFilterMaxLength" class="input input-xs mt-1" @click.stop />
              </div>
            </template>
            <template #header-channels>
              <div class="flex w-full" :style="{ direction: 'ltr', justifyContent: currentLanguage === 'he' || currentLanguage === 'ar' ? 'flex-end' : 'center' }">
                <span>{{ t('aiChats.table.headerChannels','Channels') }}</span>
              </div>
            </template>
            <template #header-agenttype>
              <div @click="toggleFilterColumn('agenttype')" class="flex flex-col cursor-pointer select-none">
                <span>{{ t('aiChats.table.headerType','Type') }}</span>
                <input v-if="activeFilterColumn === 'agenttype' || columnFilters.agenttype !== ''" v-model="columnFilters.agenttype" :maxlength="columnFilterMaxLength" class="input input-xs mt-1" @click.stop />
              </div>
            </template>
          </EasyDataTable>
        </div>
      </ThemeOnly>

      <ThemeOnly theme="modern">
        <ModernAgentTable
          class="ai-chats-modern-agent-table"
          :agents="paginatedGridAgents"
          :loading="isLoadingAgents === true"
          :style-logos="agentIdToIcon"
          :get-open-button-label="resolveOpenButtonLabelByRow"
          :get-channel-icons="getAvailableChannelDescriptorsForAgent"
          @openChat="handleOpenChat"
          @edit="handleEditClick"
          @deploy="openDeployModal"
          @status="openKnowledgeStatusModal"
          @delete="handleDeleteClick"
        />
        <div class="flex items-center justify-between mt-6 px-2">
          <div class="text-sm text-gray-500 font-medium">
            {{ t('pagination.showing', 'Showing') }} {{ (currentGridPage - 1) * gridPageSize + 1 }} - {{ Math.min(currentGridPage * gridPageSize, gridFilteredAgents.length) }} {{ t('pagination.of', 'of') }} {{ gridFilteredAgents.length }}
          </div>
          <div class="flex items-center space-x-2">
            <button 
              class="modern-pagination-btn" 
              :disabled="currentGridPage <= 1" 
              @click="currentGridPage = currentGridPage - 1"
            >
              <Icon icon="tabler:chevron-left" width="18" />
            </button>
            <div class="flex items-center px-4 h-9 rounded-xl bg-gray-50 dark:bg-gray-800 text-sm font-bold text-gray-700 dark:text-gray-300 border border-gray-100 dark:border-gray-700">
              {{ currentGridPage }} / {{ totalGridPagesGrid }}
            </div>
            <button 
              class="modern-pagination-btn" 
              :disabled="currentGridPage >= totalGridPagesGrid" 
              @click="currentGridPage = currentGridPage + 1"
            >
              <Icon icon="tabler:chevron-right" width="18" />
            </button>
          </div>
        </div>
      </ThemeOnly>
    </div>
    <div v-else class="ml-3 mt-6 w-full">
      <ThemeOnly theme="modern" :invert="true">
        <div class="w-full agents-grid px-4">
          <div v-for="item in paginatedGridAgents" :key="resolveAgentId(item)" class="agent-card">
            <div class="card bg-white dark:bg-gray-800 shadow border dark:border-gray-700 rounded-2xl p-6 cursor-pointer relative transition-transform duration-200 hover:-translate-y-0.5 hover:shadow-lg" @click="openEditForAgent(resolveAgentId(item))">
              <div class="absolute top-2 left-2">
                <span class="badge badge-outline">{{ getClassicAgentModeLabel(item) }}</span>
              </div>
              <div class="flex flex-col items-center text-center w-full mt-2">
                <div v-if="getAgentIcon(resolveAgentId(item)) !== null" class="w-16 h-16 rounded-full overflow-hidden flex items-center justify-center ring-2 ring-base-300">
                  <img :src="getAgentIcon(resolveAgentId(item))" alt="icon" class="w-16 h-16 object-cover" />
                </div>
                <div v-else class="w-16 h-16 rounded-full flex items-center justify-center text-white text-2xl font-bold ring-2 ring-base-300" :style="{ backgroundColor: getColorForAgent(item) }">
                  {{ (item.agentname !== undefined && item.agentname !== null && item.agentname.length > 0) === true ? item.agentname.charAt(0).toUpperCase() : 'A' }}
                </div>
                <div class="mt-4 w-full">
                  <div class="text-base font-semibold text-gray-900 dark:text-gray-100 truncate" :title="item.agentname">{{ item.agentname }}</div>
                </div>
                <div class="mt-1">
                  <span class="badge badge-outline">{{ t('aiChats.agentType.' + item.agenttype, item.agenttype.replace(/([A-Z])/g, ' $1').trim()) }}</span>
                </div>
                <div class="mt-3 text-sm text-gray-700 dark:text-gray-300 w-full" :title="getShortDescription(item)">{{ getShortDescription(item) }}</div>
                <div v-if="getAvailableChannelDescriptorsForAgent(item).length > 0" class="mt-4 flex flex-wrap justify-center w-full">
                  <span v-for="channel in getAvailableChannelDescriptorsForAgent(item)" :key="channel.id" class="badge badge-outline mr-2 mb-2" :title="channel.label">
                    {{ channel.label }}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </ThemeOnly>

      <ThemeOnly theme="modern">
        <div class="px-4">
          <ModernAgentGrid 
            :agents="paginatedGridAgents"
            :is-hebrew="currentLanguage === 'he' || currentLanguage === 'ar'"
            :style-logos="agentIdToIcon"
            :show-edit="true"
            :get-open-button-label="resolveOpenButtonLabelForAgent"
            :get-channel-icons="getAvailableChannelDescriptorsForAgent"
            @openChat="(agent) => handleOpenChat(agent.rowNumber)"
            @edit="(agent) => handleEditClick(agent.rowNumber)"
            @navigate="(agent) => openEditForAgent(resolveAgentId(agent))"
          />
          <div class="flex items-center justify-between mt-8 px-2">
            <div class="text-sm text-gray-500 font-medium">
              {{ t('pagination.showing', 'Showing') }} {{ (currentGridPage - 1) * gridPageSize + 1 }} - {{ Math.min(currentGridPage * gridPageSize, gridFilteredAgents.length) }} {{ t('pagination.of', 'of') }} {{ gridFilteredAgents.length }}
            </div>
            <div class="flex items-center space-x-2">
              <button 
                class="modern-pagination-btn" 
                :disabled="currentGridPage <= 1" 
                @click="currentGridPage = currentGridPage - 1"
              >
                <Icon icon="tabler:chevron-left" width="18" />
              </button>
              <div class="flex items-center px-4 h-9 rounded-xl bg-gray-50 dark:bg-gray-800 text-sm font-bold text-gray-700 dark:text-gray-300 border border-gray-100 dark:border-gray-700">
                {{ currentGridPage }} / {{ totalGridPagesGrid }}
              </div>
              <button 
                class="modern-pagination-btn" 
                :disabled="currentGridPage >= totalGridPagesGrid" 
                @click="currentGridPage = currentGridPage + 1"
              >
                <Icon icon="tabler:chevron-right" width="18" />
              </button>
            </div>
          </div>
        </div>
      </ThemeOnly>
    </div>


    <it-modal v-model="isAgentModalOpen">
    <template v-slot:body>
      <div class="bg-white rounded-lg p-2 max-w-2xl w-full" style="">
      <h2 class="text-2xl font-bold mb-4">{{ t(isEditingModal === true ? 'aiChats.editAgentModalTitle' : 'aiChats.addAgentModalTitle') }}</h2>
      <form @submit.prevent="() => {}">
          <div class="mb-4">
              <label for="profileName" class="block text-sm font-medium text-gray-700">{{ t('aiChats.agentNameLabel', 'Agent Name') }}</label>
              <input v-model="agentName" type="text" id="" maxlength="30" required class="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm">
          </div>
          <div class="mb-4">
              <label for="profileName" class="block text-sm font-medium text-gray-700">{{ t('aiChats.descriptionLabel', 'Description') }}</label>
              <textarea v-model="agentDescription" id="" maxlength="30" required class="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"></textarea>
          </div>

          <div class="form-control">
    <label class="label cursor-pointer">
      <span class="label-text">{{ t('aiChats.activeAgentLabel', 'Active Agent') }}</span>
      <input type="checkbox" checked="checked" class="checkbox" />
    </label>
  </div>

          <div class="flex justify-end mt-6">
              <button type="button" @click="isAgentModalOpen = false" class="mr-3 inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-gray-700 bg-gray-100 hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500">
                  {{ t('aiChats.cancelButton', 'Cancel') }}
              </button>
              <button @click="router.push('/aiChatAgentSetup')" type="submit" class="inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500">
                  {{ t('aiChats.addAgentButton', 'Add Agent') }}
            </button>
          </div>
      </form>
  </div>
    </template>
  </it-modal>


  <it-modal v-model="isDeleteModalOpen">
    <div class="p-4">
      <h2 class="text-2xl font-bold mb-4">{{ t('aiChats.deleteConfirmTitle', 'Are you sure?') }}</h2>
      <span>{{ t('aiChats.deleteConfirmText', 'Are you sure you want to delete this agent?') }}</span>

      <div class="flex justify-end mt-6">
        <button @click="isDeleteModalOpen=false" type="button" class="mr-3 inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-gray-700 bg-gray-100 hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500">
          {{ t('aiChats.cancelButton', 'Cancel') }}
        </button>
        <button @click="deleteAgent(selectedAgentId)" type="submit" class="inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500">
          {{ t('aiChats.deleteButton', 'Delete') }}
        </button>
      </div>
    </div>
  </it-modal>

  <it-modal v-model="isLimitReachedModalOpen">
    <div class="p-4 text-center">
      <h2 class="text-2xl font-bold mb-4">{{ t('aiChats.limitReachedModal.title', 'Limit Reached') }}</h2>
      <p>{{ t('aiChats.limitReachedModal.message', 'You have reached the maximum number of agents for your organization.') }}</p>
      <div class="flex justify-center mt-6">
        <button @click="isLimitReachedModalOpen = false" class="btn">
          {{ t('aiChats.limitReachedModal.closeButton', 'Close') }}
        </button>
      </div>
    </div>
  </it-modal>

  <it-modal v-model="isProcessingDeleteModalOpen">
    <div class="p-6 text-center">
      <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-12 h-12 text-yellow-500 mx-auto mb-4">
        <path stroke-linecap="round" stroke-linejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.008v.008H12v-.008z" />
      </svg>
      <h2 class="text-xl font-semibold mb-2">{{ t('aiChats.processingDeletionModal.title', 'Processing Deletion') }}</h2>
      <p class="text-gray-600 dark:text-gray-400 mb-6">
        {{ t('aiChats.processingDeletionModal.text', 'Please wait, the system is processing the deletion of previous agents. Try again at a later time.') }}
      </p>
      <button @click="isProcessingDeleteModalOpen = false" type="button" class="inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500">
        {{ t('aiChats.processingDeletionModal.okButton', 'OK') }}
      </button>
    </div>
  </it-modal>

  <div v-if="isCreateOptionsModalOpen !== false" class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 modal-overlay">
    <div class="bg-white rounded-2xl shadow-2xl max-w-7xl w-full mx-4 p-8 transform transition-all duration-300" style="background-image: linear-gradient(rgba(229, 231, 235, 0.45) 1px, transparent 1px), linear-gradient(90deg, rgba(229, 231, 235, 0.45) 1px, transparent 1px); background-size: 24px 24px;">
      <transition :name="manualStepDirection === 'forward' ? 'create-mode-step-forward' : 'create-mode-step-back'" mode="out-in">
        <div v-if="isManualModeOptionsOpen !== true" key="create-mode-main-header" class="text-center mb-8">
          <h2 class="text-3xl font-bold text-gray-900 mb-3">{{ t('aiChats.chooseMode', 'Choose Your Setup Mode') }}</h2>
          <p class="text-gray-600 text-base leading-relaxed">{{ t('aiChats.chooseModeDescription', 'Select how you want to configure your AI agent. Each mode is designed for different levels of customization.') }}</p>
        </div>
        <div v-else key="create-mode-manual-header" class="text-center mb-8">
          <h2 class="text-3xl font-bold text-gray-900 mb-3">{{ t('aiChats.chooseManualMode', 'Choose a manual setup') }}</h2>
          <p class="text-gray-600 text-base leading-relaxed">{{ t('aiChats.chooseManualModeDescription', 'Choose the level of control you want over your agent.') }}</p>
        </div>
      </transition>
      <div class="mode-cards-stage mb-6 w-full">
        <transition :name="manualStepDirection === 'forward' ? 'create-mode-step-forward' : 'create-mode-step-back'" mode="out-in">
          <div v-if="isManualModeOptionsOpen !== true" key="create-mode-main-cards" class="mode-cards-row w-full">
            <div id="create-mode-manual" @click="openManualModeOptions"
                 class="border-2 rounded-xl p-6 cursor-pointer transition-all duration-200 hover:shadow-md mx-2 mode-card border-gray-200 bg-white hover:border-gray-300 create-mode-step-card">
              <div class="h-48 mb-4 rounded-lg overflow-hidden bg-gray-100 flex items-center justify-center">
                <div class="w-16 h-16 rounded-2xl border border-gray-200 bg-white flex items-center justify-center shadow-sm">
                  <Icon icon="tabler:tools" width="30" class="text-gray-700" />
                </div>
              </div>
              <div class="space-y-3 text-center">
                <h3 class="text-xl font-semibold text-gray-900">{{ t('aiChats.manualMode', 'Manually') }}</h3>
                <p class="text-gray-600 text-sm leading-relaxed">{{ t('aiChats.manualModeDescription', 'Configure your agent yourself with the setup that fits your needs.') }}</p>
              </div>
            </div>
            <div id="create-mode-automatic" @click="selectedMode = 'automatic'"
                 :class="selectedMode === 'automatic' ? 'border-gray-500 bg-gray-50' : 'border-gray-200 bg-white hover:border-gray-300'"
                 class="border-2 rounded-xl p-6 cursor-pointer transition-all duration-200 hover:shadow-md mx-2 mode-card create-mode-step-card create-mode-step-card-delayed">
              <div class="h-48 mb-4 rounded-lg overflow-hidden bg-gray-100 dark:bg-gray-800 flex items-center justify-center">
                <div class="w-16 h-16 rounded-2xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 flex items-center justify-center shadow-sm">
                  <Icon :key="'automatic-mode-ai-stars'" icon="mingcute:ai-fill" width="30" class="text-gray-700 dark:text-gray-200" />
                </div>
              </div>
              <div class="space-y-3 text-center">
                <h3 class="text-xl font-semibold text-gray-900">{{ t('aiChats.recommendedMode', 'Recommended: AI Creation') }}</h3>
                <p class="text-gray-600 text-sm leading-relaxed">{{ t('aiChats.automaticModeDescription', 'Our general agent will create the agent automatically by looking at your website and exploring your brand and business.') }}</p>
              </div>
            </div>
          </div>
          <div v-else key="create-mode-manual-cards" class="mode-cards-row w-full">
            <div id="create-mode-light" @click="selectedMode = 'light'"
                 :class="selectedMode === 'light' ? 'border-gray-500 bg-gray-50' : 'border-gray-200 bg-white hover:border-gray-300'"
                 class="border-2 rounded-xl p-6 cursor-pointer transition-all duration-200 hover:shadow-md mx-2 mode-card create-mode-step-card">
              <div class="h-48 mb-4 rounded-lg overflow-hidden bg-gray-100 flex items-center justify-center">
                <svg xmlns="http://www.w3.org/2000/svg" class="w-16 h-16" viewBox="0 0 24 24">
                  <path fill="#020406" d="m20.713 8.128l-.246.566a.506.506 0 0 1-.934 0l-.246-.566a4.36 4.36 0 0 0-2.22-2.25l-.759-.339a.53.53 0 0 1 0-.963l.717-.319a4.37 4.37 0 0 0 2.251-2.326l.253-.611a.506.506 0 0 1 .942 0l.253.61a4.37 4.37 0 0 0 2.25 2.327l.718.32a.53.53 0 0 1 0 .962l-.76.338a4.36 4.36 0 0 0-2.219 2.251M10 3h4v2h-4a6 6 0 0 0-6 6c0 3.61 2.462 5.966 8 8.48V17h2a6 6 0 0 0 6-6h2a8 8 0 0 1-8 8v3.5c-5-2-12-5-12-11.5a8 8 0 0 1 8-8"/>
                </svg>
              </div>
              <div class="space-y-3 text-center">
                <h3 class="text-xl font-semibold text-gray-900">{{ t('aiChats.lightMode','Light Mode') }}</h3>
                <p class="text-gray-600 text-sm leading-relaxed">{{ t('aiChats.lightModeDescription','An easier agent setup, tailored for people that don\'t want to deal with prompts; we create the agent for them with a much simpler UI.') }}</p>
              </div>
            </div>
            <div id="create-mode-standard" @click="selectedMode = 'standard'"
                 :class="selectedMode === 'standard' ? 'border-gray-500 bg-gray-50' : 'border-gray-200 bg-white hover:border-gray-300'"
                 class="border-2 rounded-xl p-6 cursor-pointer transition-all duration-200 hover:shadow-md mx-2 mode-card create-mode-step-card create-mode-step-card-delayed">
              <div class="h-48 mb-4 rounded-lg overflow-hidden bg-gray-100 flex items-center justify-center">
                <svg xmlns="http://www.w3.org/2000/svg" class="w-16 h-16" viewBox="0 0 24 24">
                  <g fill="none" stroke="#020406" stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5">
                    <path d="M10.014 2c-3.778 0-5.667 0-6.84 1.172S2 6.229 2 10s0 5.657 1.174 6.828S6.236 18 10.014 18h4.007c3.778 0 5.667 0 6.84-1.172c.81-.808 1.061-1.956 1.139-3.828m-10 5v4m-4 0h8m-5-7h2"/>
                    <path d="M18 4h-2c-.943 0-1.414 0-1.707.293S14 5.057 14 6v2c0 .943 0 1.414.293 1.707S15.057 10 16 10h2c.943 0 1.414 0 1.707-.293S20 8.943 20 8V6c0-.943 0-1.414-.293-1.707S18.943 4 18 4m-2.5 6v2m3-2v2m-3-10v2m3-2v2M14 5.5h-2m2 3h-2m10-3h-2m2 3h-2"/>
                  </g>
                </svg>
              </div>
              <div class="space-y-3 text-center">
                <h3 class="text-xl font-semibold text-gray-900">{{ t('aiChats.standardMode', 'Regular') }}</h3>
                <p class="text-gray-600 text-sm leading-relaxed">{{ t('aiChats.standardModeDescription','A step for people that want to customize their agent to the fullest, with control over prompt.') }}</p>
              </div>
            </div>
          </div>
        </transition>
      </div>
      <div class="flex justify-center mb-4">
        <button @click="handleCancel"
                :class="(currentLanguage === 'ar' || currentLanguage === 'he') ? 'flex-1' : 'flex-1 mr-4'"
                class="btn">
          {{ isManualModeOptionsOpen === true ? t('aiChats.back', 'Back') : t('aiChats.cancel', 'Cancel') }}
        </button>
        
        <button @click="handleContinue"
                :disabled="selectedMode === null"
                :class="(currentLanguage === 'ar' || currentLanguage === 'he') ? 'flex-1 mr-4' : 'flex-1'"
                class="btn">
          {{ t('aiChats.continue','Continue') }}
        </button>
      </div>
      
      <div class="text-center">
        <p class="text-sm text-gray-500 italic">{{ t('aiChats.switchNote','You can always switch between modes later') }}</p>
      </div>
    </div>
  </div>

  <div v-if="isKnowledgeStatusModalOpen !== false" 
       class="fixed inset-0 z-50 overflow-y-auto flex items-center justify-center bg-black bg-opacity-50 transition-opacity duration-300 ease-in-out"
       @click.self="isKnowledgeStatusModalOpen = false">
    <div class="bg-base-100 dark:bg-gray-800 rounded-lg shadow-xl transform transition-all sm:my-8 sm:align-middle sm:w-full sm:max-w-2xl md:max-w-3xl lg:max-w-4xl m-6" 
         role="dialog" aria-modal="true" aria-labelledby="knowledge-status-modal-title">
      
      <div class="px-6 py-4 border-b border-base-300 dark:border-gray-700">
        <h2 id="knowledge-status-modal-title" class="text-xl font-semibold text-gray-900 dark:text-gray-100">
          <span>{{ t('aiChats.knowledgeStatusModal.titlePrefix', 'Knowledge Base Status for:') }}</span> {{ (currentAgentForStatus !== false && currentAgentForStatus.agentname) !== false ? currentAgentForStatus.agentname : t('aiChats.knowledgeStatusModal.defaultAgentName','Agent') }}
        </h2>
      </div>

      <div class="px-6 py-4">
        <div class="flex justify-between items-center mb-4">
          <div>
            <button
              @click="openKnowledgeSearchModal()"
              class="btn btn-sm"
              :disabled="currentAgentForStatus === null || currentAgentForStatus === undefined">
              <Icon icon="tabler:search" class="mr-1 h-4 w-4"></Icon>
              <span>{{ t('aiChats.knowledgeStatusModal.searchByLocationButton', 'Search by Location') }}</span>
            </button>
          </div>
          <button 
            @click="currentAgentForStatus !== false && fetchKnowledgeStatus(currentAgentForStatus.agentid) !== false" 
            class="btn btn-sm btn-outline"
            :disabled="isLoadingKnowledgeStatus === true">
            <Icon icon="tabler:refresh" class="mr-1 h-4 w-4"></Icon>
            <span v-if="isLoadingKnowledgeStatus === true" class="loading loading-spinner loading-xs mr-2"></span>
            <span>{{ t('aiChats.knowledgeStatusModal.refreshButton', 'Refresh Status') }}</span>
          </button>
        </div>

        <div v-if="knowledgeStatusData.length > 0" class="mb-4">
          <div class="flex flex-wrap -mx-1">
            <div class="w-1/2 md:w-1/4 px-1 mb-2">
              <div class="rounded-lg border border-base-300 dark:border-gray-700 bg-base-100 dark:bg-gray-800 p-3 shadow">
                <div class="text-xs text-gray-500 dark:text-gray-400">{{ t('aiChats.knowledgeStatusModal.cardTotal', 'Total Files') }}</div>
                <div class="text-2xl font-bold">{{ knowledgeSummary.total }}</div>
              </div>
            </div>
            <div class="w-1/2 md:w-1/4 px-1 mb-2">
              <div class="rounded-lg border border-base-300 dark:border-gray-700 bg-base-100 dark:bg-gray-800 p-3 shadow">
                <div class="text-xs text-gray-500 dark:text-gray-400">{{ t('aiChats.knowledgeStatusModal.cardAvailable', 'Available') }}</div>
                <div class="text-2xl font-bold text-green-600 dark:text-green-400">{{ knowledgeSummary.available }}</div>
              </div>
            </div>
            <div class="w-1/2 md:w-1/4 px-1 mb-2">
              <div class="rounded-lg border border-base-300 dark:border-gray-700 bg-base-100 dark:bg-gray-800 p-3 shadow">
                <div class="text-xs text-gray-500 dark:text-gray-400">{{ t('aiChats.knowledgeStatusModal.cardProcessing', 'Processing') }}</div>
                <div class="text-2xl font-bold text-blue-600 dark:text-blue-400">{{ knowledgeSummary.processing }}</div>
              </div>
            </div>
            <div class="w-1/2 md:w-1/4 px-1 mb-2">
              <div class="rounded-lg border border-base-300 dark:border-gray-700 bg-base-100 dark:bg-gray-800 p-3 shadow">
                <div class="text-xs text-gray-500 dark:text-gray-400">{{ t('aiChats.knowledgeStatusModal.cardIssues', 'Issues') }}</div>
                <div class="text-2xl font-bold text-red-600 dark:text-red-400">{{ knowledgeSummary.issues }}</div>
              </div>
            </div>
          </div>
        </div>

        <div v-if="isLoadingKnowledgeStatus === true && knowledgeStatusData.length === 0" class="overflow-x-auto">
          <table class="table table-zebra table-pin-rows w-full">
            <thead>
              <tr>
                <th class="w-1/3">{{ t('aiChats.knowledgeStatusModal.tableHeaderName', 'Name') }}</th>
                <th class="w-1/6">{{ t('aiChats.knowledgeStatusModal.tableHeaderType', 'Type') }}</th>
                <th class="w-1/6">{{ t('aiChats.knowledgeStatusModal.tableHeaderStatus', 'Status') }}</th>
                <th class="w-1/6">{{ t('aiChats.knowledgeStatusModal.tableHeaderReason', 'Reason') }}</th>
                <th class="w-1/6">{{ t('aiChats.knowledgeStatusModal.tableHeaderOptions', 'Options') }}</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="n in 5" :key="`knowledge-status-skeleton-${n}`">
                <td><div class="skeleton h-4 w-11/12"></div></td>
                <td><div class="skeleton h-4 w-16"></div></td>
                <td><div class="skeleton h-6 w-24 rounded-full"></div></td>
                <td><div class="skeleton h-4 w-10/12"></div></td>
                <td><div class="skeleton h-8 w-full"></div></td>
              </tr>
            </tbody>
          </table>
        </div>
        <div v-else-if="knowledgeStatusData.length === 0 && isLoadingKnowledgeStatus === false" class="text-center p-4 text-gray-500">
          {{ t('aiChats.knowledgeStatusModal.noSources', 'No knowledge sources configured or status not yet available.') }}
        </div>
        <div v-else class="overflow-x-auto">
          <table class="table table-zebra table-pin-rows w-full">
            <thead>
              <tr>
                <th class="w-1/3">{{ t('aiChats.knowledgeStatusModal.tableHeaderName', 'Name') }}</th> 
                <th class="w-1/6">{{ t('aiChats.knowledgeStatusModal.tableHeaderType', 'Type') }}</th>
                <th class="w-1/6">{{ t('aiChats.knowledgeStatusModal.tableHeaderStatus', 'Status') }}</th>
                <th class="w-1/6">{{ t('aiChats.knowledgeStatusModal.tableHeaderReason', 'Reason') }}</th>
                <th class="w-1/6">{{ t('aiChats.knowledgeStatusModal.tableHeaderOptions', 'Options') }}</th>
              </tr>
              <tr>
                <th><input v-model="columnFiltersModal.name" :maxlength="modalFilterMaxLength" class="input input-xs w-full" placeholder="Filter Name" /></th>
                <th>
                  <select v-model="columnFiltersModal.type" class="select select-xs w-full">
                    <option value="">{{ t('aiChats.knowledgeStatusModal.typeFilterAll','All Types') }}</option>
                    <option v-for="opt in typeOptions" :key="opt.value" :value="opt.value">{{ opt.label }}</option>
                  </select>
                </th>
                <th>
                  <select v-model="columnFiltersModal.status" class="select select-xs w-full">
                    <option value="">{{ t('aiChats.knowledgeStatusModal.statusFilterAll', 'All Statuses') }}</option>
                    <option v-for="opt in statusFilterOptions" :key="opt.value" :value="opt.value">{{ opt.label }}</option>
                  </select>
                </th>
                <th><input v-model="columnFiltersModal.reason" :maxlength="modalFilterMaxLength" class="input input-xs w-full" placeholder="Filter Reason" /></th>
              </tr>
            </thead>
            <tbody>
              <template v-for="(item, index) in paginatedKnowledgeStatusData" :key="index">
                <tr>
                  <td class="truncate max-w-xs" :title="item.name">{{ item.name }}</td>
                  <td>{{ item.type }}</td>
                  <td>
                    <span :class="{
                      'badge badge-success': item.status === 'Processed' || item.status === 'Completed',
                      'badge badge-info': item.status === 'Processing' || item.status === 'Scraping In Progress' || item.status === 'Discovering Links',
                      'badge badge-warning': item.status === 'Pending / Error' || item.status === 'PartiallyCompleted',
                      'badge badge-error': item.status === 'Error' || item.status === 'Failed',
                      'badge': item.status === 'Unknown'
                    }">{{ (item.status === 'Processed' || item.status === 'Completed') ? 'Available' : item.status }}</span>
                    <div v-if="item.progress !== undefined && item.progress !== null && item.progress.total > 0 && (['website','websiteurl','websitepage'].includes(item.type) === true) && (['Processing','Scraping In Progress','Discovering Links','PartiallyCompleted'].includes(item.status) === true)" class="text-xs text-gray-500 dark:text-gray-400 mt-1">
                      <span>{{ t('aiChats.knowledgeStatusModal.scrapedOf', 'Scraped') }}</span> {{ item.progress.current }} <span>{{ t('aiChats.knowledgeStatusModal.of', 'of') }}</span> {{ item.progress.total }}
                      <span v-if="item.progress.total > 0">({{ item.progress.percentage }}%)</span>
                      <progress v-if="item.progress.total > 0" class="progress progress-info w-full h-1.5 mt-1" :value="item.progress.current" :max="item.progress.total"></progress>
                    </div>
                  </td>
                  <td class="text-xs truncate" :class="{ 'text-error': item.status === 'Error' || item.status === 'Failed' }" :title="getVisibleKnowledgeStatusReason(item.details)">
                    <template v-if="['Processing','Scraping In Progress','Discovering Links'].includes(item.status) === false">
                      {{ getVisibleKnowledgeStatusReason(item.details) }}
                    </template>
                  </td>
                  <td class="text-center">
                    <div v-if="['website','websiteurl','websitepage'].includes(item.type) !== false && (item.status !== 'Processed' && item.status !== 'Completed')" class="flex w-full items-center justify-between">
                      <button @click="openScrapeReport(item)" class="btn btn-sm flex-1 mx-1">
                        {{ t('aiChats.knowledgeStatusModal.reportButton', 'Report') }}
                      </button>
                      <button @click="continueWebsiteScan(item)" class="btn btn-sm btn-outline flex-1 mx-1">
                        {{ t('aiChats.knowledgeStatusModal.continueButton', 'Continue') }}
                      </button>
                      <button @click="retryFailedWebsiteScan(item)" class="btn btn-sm btn-outline flex-1 mx-1">
                        {{ t('aiChats.knowledgeStatusModal.retryFailedButton', 'Retry Failed') }}
                      </button>
                    </div>
                  </td>
                </tr>
              </template>
            </tbody>
          </table>
          <div class="flex flex-wrap justify-between items-center mt-4">
            <button class="btn btn-sm" :disabled="currentStatusPage <= 1" @click="setStatusPage(currentStatusPage - 1)"><Icon :icon="(currentLanguage === 'he' || currentLanguage === 'ar') ? 'tabler:chevron-right' : 'tabler:chevron-left'" class="h-4 w-4"></Icon></button>
            <div class="flex items-center mx-2 my-1">
              <span class="text-sm mr-2">{{ currentStatusPage }}/{{ safeTotalStatusPages }}</span>
              <label class="input input-bordered input-sm flex items-center w-32">
                <input v-model="statusPageInput" type="number" min="1" :max="safeTotalStatusPages" class="grow w-full" :placeholder="t('aiChats.knowledgeStatusModal.pageInputPlaceholder', 'Page')" @keyup.enter="jumpToStatusPage()" @change="jumpToStatusPage()" />
              </label>
              <button class="btn btn-sm ml-2" @click="jumpToStatusPage()">{{ t('aiChats.knowledgeStatusModal.pageJumpButton', 'Go') }}</button>
            </div>
            <button class="btn btn-sm" :disabled="currentStatusPage >= safeTotalStatusPages" @click="setStatusPage(currentStatusPage + 1)"><Icon :icon="(currentLanguage === 'he' || currentLanguage === 'ar') ? 'tabler:chevron-left' : 'tabler:chevron-right'" class="h-4 w-4"></Icon></button>
          </div>
        </div>
      </div>  

      <div class="px-6 py-4 bg-base-200 dark:bg-gray-750 text-right border-t border-base-300 dark:border-gray-700 rounded-b-lg">
        <button @click="isKnowledgeStatusModalOpen = false" class="btn">{{ t('aiChats.knowledgeStatusModal.closeButton', 'Close') }}</button>
      </div>
    </div>
  </div>
  <div
    v-if="isKnowledgeSearchModalOpen !== false"
    class="fixed inset-0 z-[60] overflow-y-auto flex items-center justify-center bg-black bg-opacity-50 transition-opacity duration-300 ease-in-out"
    @click.self="closeKnowledgeSearchModal()">
    <div
      class="bg-base-100 dark:bg-gray-800 rounded-lg shadow-xl transform transition-all w-[96vw] max-w-6xl h-[86vh] m-4 flex flex-col"
      role="dialog"
      aria-modal="true"
      aria-labelledby="knowledge-search-modal-title">
      <div class="px-6 py-4 border-b border-base-300 dark:border-gray-700 flex items-center justify-between">
        <h2 id="knowledge-search-modal-title" class="text-xl font-semibold text-gray-900 dark:text-gray-100">
          {{ t('aiChats.knowledgeSearchModal.title', 'Knowledge Base Location Search') }}
        </h2>
        <button class="btn btn-sm" @click="closeKnowledgeSearchModal()">{{ t('aiChats.knowledgeStatusModal.closeButton', 'Close') }}</button>
      </div>
      <div class="px-6 py-4 border-b border-base-300 dark:border-gray-700">
        <div class="flex flex-col md:flex-row md:items-center md:space-x-3">
          <input
            v-model="knowledgeSearchLocationInput"
            :maxlength="modalFilterMaxLength"
            class="input input-bordered w-full"
            :placeholder="t('aiChats.knowledgeSearchModal.locationPlaceholder', 'Enter location...')"
            @keyup.enter="runKnowledgeLocationSearch()" />
          <button
            class="btn mt-3 md:mt-0"
            @click="runKnowledgeLocationSearch()"
            :disabled="isSearchingKnowledgeByLocation === true">
            <span v-if="isSearchingKnowledgeByLocation === true" class="loading loading-spinner loading-xs mr-2"></span>
            {{ t('aiChats.knowledgeSearchModal.searchButton', 'Search') }}
          </button>
        </div>
        <div v-if="knowledgeSearchError.length > 0" class="text-error text-sm mt-2">{{ knowledgeSearchError }}</div>
      </div>
      <div class="flex-1 overflow-y-auto px-6 py-4">
        <div v-if="isSearchingKnowledgeByLocation === true" class="text-center p-6">
          <span class="loading loading-dots loading-lg"></span>
          <p>{{ t('aiChats.knowledgeSearchModal.searching', 'Searching location in knowledge base...') }}</p>
        </div>
        <div v-else-if="knowledgeSearchResults.length === 0" class="text-center p-6 text-gray-500 dark:text-gray-400">
          {{ t('aiChats.knowledgeSearchModal.emptyState', 'No search results yet. Enter a location and click Search.') }}
        </div>
        <div v-else class="space-y-3">
          <div
            v-for="(result, index) in knowledgeSearchResults"
            :key="`${result.source}-${index}`"
            class="rounded-lg border border-base-300 dark:border-gray-700 bg-base-100 dark:bg-gray-900 p-4">
            <div class="text-sm font-semibold text-primary mb-2">{{ result.source }}</div>
            <div class="text-sm text-gray-800 dark:text-gray-100 whitespace-pre-wrap break-words">{{ result.snippet }}</div>
          </div>
        </div>
      </div>
      <div class="px-6 py-4 bg-base-200 dark:bg-gray-750 text-right border-t border-base-300 dark:border-gray-700 rounded-b-lg">
        <button @click="closeKnowledgeSearchModal()" class="btn">{{ t('aiChats.knowledgeStatusModal.closeButton', 'Close') }}</button>
      </div>
    </div>
  </div>
  <div v-if="isImportExistingModalOpen !== false" class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
    <div class="bg-white rounded-xl p-6 w-full max-w-lg" role="dialog" aria-modal="true">
      <h3 class="text-xl font-semibold mb-4">{{ t('aiChats.importExistingModal.title', 'Import Existing Agent') }}</h3>
      <div class="mb-4">
        <label class="block text-sm font-medium mb-2">{{ t('aiChats.importExistingModal.agentLabel', 'Select target agent') }}</label>
        <select v-model="importTargetAgentId" class="select select-bordered w-full" :disabled="isImportingExistingAgent === true">
          <option value="" disabled>{{ t('aiChats.importExistingModal.agentPlaceholder', 'Choose an agent') }}</option>
          <option v-for="agent in aiAgents" :key="String(agent.agentid)" :value="String(agent.agentid)">
            {{ agent.agentname }}
          </option>
        </select>
      </div>
      <div class="mb-2">
        <label class="block text-sm font-medium mb-2">{{ t('aiChats.importExistingModal.fileLabel', 'Agent zip file') }}</label>
        <input type="file" accept=".zip,application/zip" class="file-input file-input-bordered w-full" @change="onImportExistingFileChange" :disabled="isImportingExistingAgent === true" />
      </div>
      <div class="text-xs opacity-70 mb-2" v-if="importExistingFileName.length > 0">{{ importExistingFileName }}</div>
      <div class="text-error text-sm mb-2" v-if="importExistingError.length > 0">{{ importExistingError }}</div>
      <div class="text-success text-sm mb-2" v-if="importExistingSuccess.length > 0">{{ importExistingSuccess }}</div>
      <div class="flex justify-end mt-5">
        <button @click="closeImportExistingModal" class="btn mr-2" :disabled="isImportingExistingAgent === true">{{ t('aiChats.cancelButton', 'Cancel') }}</button>
        <button @click="submitImportExistingAgent" class="btn" :disabled="isImportingExistingAgent === true || importTargetAgentId.length === 0 || importExistingFile === null">
          <span v-if="isImportingExistingAgent === true" class="loading loading-spinner loading-sm mr-2"></span>
          {{ t('aiChats.importExistingOption', 'Import Existing') }}
        </button>
      </div>
    </div>
  </div>

  <it-modal v-model="showProcessingModal">
    <template v-slot:body>
      <div class="p-4">
        <div class="flex flex-col items-center text-center">
          <svg xmlns="http://www.w3.org/2000/svg" class="h-12 w-12 text-info mx-auto mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
            <path stroke-linecap="round" stroke-linejoin="round" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <h2 class="text-xl font-bold mb-4">{{ t('aiChats.processingFilesModal.title', 'Files are being processed') }}</h2>
          <p class="text-gray-600 dark:text-gray-400">{{ t('aiChats.processingFilesModal.message', 'Your files are currently being processed. You can check the status by clicking the Status button.') }}</p>
        </div>
        <div class="flex justify-end mt-4">
          <button class="btn" @click="showProcessingModal = false">{{ t('aiChats.processingFilesModal.closeButton', 'Close') }}</button>
        </div>
      </div>
    </template>
  </it-modal>

  
  <it-modal v-model="showScrapeReportModal" width="min(90vw, 1200px)">
    <template v-slot:body>
      <div v-if="scrapeReportSource !== null && currentAgentForStatus !== null" class="max-h-[85vh] overflow-y-auto p-4 sm:p-6">
        <WebsiteScrapeReport
          :agent-id="currentAgentForStatus.agentid"
          :source="scrapeReportSource"
          :initial-open="true"
          :hide-toggle="true"
        />
        <div class="flex justify-end mt-6">
          <button class="btn" @click="closeScrapeReportModal()">{{ t('aiChats.knowledgeStatusModal.closeButton','Close') }}</button>
        </div>
      </div>
    </template>
  </it-modal>

  <OpenAgentChannelsModal
    :visible="showOpenAgentModal === true"
    :isRtl="currentLanguage === 'he' || currentLanguage === 'ar'"
    :channels="openAgentModalChannels"
    @close="closeOpenAgentModal"
    @open="openSelectedAgentChannel"
  />

  <DeployAgentModal v-model="isDeployModalOpen" :agent="currentAgentForDeploy" :api-endpoint="apiEndpoint" />

    </div>
  </ThemeOnly>
</template>
<style src="@vueform/multiselect/themes/default.css"></style>
<style>
table {
  font-family: "Heebo", sans-serif !important;
}
.v-dropdown-trigger{
  width:100% !important;
  }
.agentStatus {
  padding: 0.25rem 0.75rem;
  background: #dcfce7;
  color: #166534;
  border-radius: 9999px;
  font-size: 0.875rem;
  font-weight: 500;
}
.inactiveAgent{
  --tw-bg-opacity: 0.4;
  background-color: rgb(239 68 68 / var(--tw-bg-opacity)) !important;
  color:white;
}
.sp-trigger-container .sp-trigger.sp-chips{
  max-height: 1.8rem;
  overflow: auto;
}
.sp-trigger-container{
  height:3rem;
}
.dp__main input {
 padding-left:17%;
border-color: #dadcdf;
flex-shrink: 1;
appearance: none;
height: 3rem;
width:260px;
font-size: 1rem;
line-height: 2;
line-height: 1.5rem;
border-radius: var(--rounded-btn, .5rem);
border-width: 1px;

--tw-bg-opacity: 1;
background-color: var(--fallback-b1,oklch(var(--b1)/var(--tw-bg-opacity)));
}

.multiselect{
  height:3rem;
  width: 180px;
  margin-right:1%;
}

.agentType {
  padding: 0.25rem 0.75rem;
  color: white;
  border-radius: 9999px;
  font-size: 0.875rem;
  font-weight: 500;
}
.agents-grid{
  display:grid;
  grid-template-columns:repeat(5,minmax(0,1fr));
}
.agent-card{padding:8px}
@media (max-width:1536px){.agents-grid{grid-template-columns:repeat(4,minmax(0,1fr))}}
@media (max-width:1280px){.agents-grid{grid-template-columns:repeat(3,minmax(0,1fr))}}
@media (max-width:1024px){.agents-grid{grid-template-columns:repeat(2,minmax(0,1fr))}}
@media (max-width:640px){.agents-grid{grid-template-columns:repeat(1,minmax(0,1fr))}}
.mode-cards-stage{
  position:relative;
  min-height:22rem;
}
.mode-cards-row{
  display:flex;
  flex-wrap:nowrap;
  justify-content:center;
  align-items:stretch;
  width:100%;
  overflow-x:auto;
}
.mode-card{
  flex:1 1 0;
  width:25%;
  min-width:14rem;
  max-width:none;
}
.create-mode-step-forward-enter-active,
.create-mode-step-forward-leave-active,
.create-mode-step-back-enter-active,
.create-mode-step-back-leave-active{
  transition:opacity 320ms ease, transform 320ms ease;
}
.create-mode-step-forward-enter-from{
  opacity:0;
  transform:translateX(28px);
}
.create-mode-step-forward-leave-to{
  opacity:0;
  transform:translateX(-28px);
}
.create-mode-step-back-enter-from{
  opacity:0;
  transform:translateX(-28px);
}
.create-mode-step-back-leave-to{
  opacity:0;
  transform:translateX(28px);
}
.create-mode-step-forward-enter-active .create-mode-step-card,
.create-mode-step-back-enter-active .create-mode-step-card{
  animation:create-mode-card-in 360ms ease forwards;
}
.create-mode-step-forward-enter-active .create-mode-step-card-delayed,
.create-mode-step-back-enter-active .create-mode-step-card-delayed{
  animation-delay:80ms;
}
@keyframes create-mode-card-in{
  from{
    opacity:0;
    transform:translateY(14px) scale(0.98);
  }
  to{
    opacity:1;
    transform:translateY(0) scale(1);
  }
}
.ai-chats-view-shell {
  display: flex;
  flex-direction: column;
  height: 100%;
  min-height: 0;
  overflow: hidden;
  box-sizing: border-box;
}
.ai-chats-view-header {
  flex-shrink: 0;
}
.ai-chats-view-body {
  flex: 1 1 auto;
  min-height: 0;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}
.ai-chats-table-shell {
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: hidden;
}
.ai-chats-table-shell .vue3-easy-data-table {
  height: 100% !important;
  min-height: 0;
  display: flex;
  flex-direction: column;
}
.ai-chats-table-shell .vue3-easy-data-table__main {
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto !important;
  overflow-x: auto;
}
.ai-chats-modern-agent-table {
  flex: 1 1 auto;
  min-height: 0;
}
</style>
<script setup>
import { ref, onMounted, onBeforeUnmount, computed, getCurrentInstance, nextTick, watch } from 'vue';
import EasyDataTable from "vue3-easy-data-table";
import Multiselect from '@vueform/multiselect'
import { SelectPageList } from 'v-selectpage'
import PasswordInputField from '@/components/PasswordInputField.vue'
import router from '@/router';
import axios from '@/api/axiosInstance';
import { reactive } from 'vue';
import { globalState } from '@/globalState';
import { Icon } from '@iconify/vue/dist/offline';
import { useToast } from 'vue-toastification';
import { useI18n } from '@/i18n';
import { useRoute } from 'vue-router';
import WebsiteScrapeReport from '@/components/WebsiteScrapeReport.vue';
import DeployAgentModal from '@/components/DeployAgentModal.vue';
import ModernAgentGrid from '@/components/ModernAgentGrid.vue';
import ModernAgentTable from '@/components/ModernAgentTable.vue';
import ModernLoader from '@/components/ModernLoader.vue';
import ModernAIChats from '@/Themes/modern/views/AIChats.vue';
import LightAIChats from '@/Themes/light/views/AIChats.vue';
import ThemeOnly from '@/components/ThemeOnly.vue';
import OpenAgentChannelsModal from '@/shared/components/OpenAgentChannelsModal.vue';
import { getAllAIChats } from '@/shared/services/aiChatsService';
import { buildOpenAgentChannels, getOpenAgentButtonLabel, normalizeOpenAgentPhoneDigits, normalizeOpenAgentSelectedChannels, normalizeOpenAgentTelValue, shouldOpenSingleAgentChannelDirectly } from '@/shared/utils/openAgentChannels.js';
import { resolveOpenWebAgentTarget, resolveOpenWebVoiceAgentTarget } from '@/shared/utils/openAgentTargets.js';

const { t, currentLanguage } = useI18n();
const toast = useToast();
const route = useRoute();
const instance = getCurrentInstance();
const aiChatsRoot = ref(null);
const createDropdownRef = ref(null);
const isCreateDropdownOpen = ref(false);
const searchMaxLength = 120;
const columnFilterMaxLength = 80;
const modalFilterMaxLength = 120;
const urlMaxLength = 2048;

function getUserRolesFromCookies() {
  try {
    const token = document.cookie.split('; ').find(row => row.startsWith('jwtToken=') !== false)?.split('=')[1];
    if (token !== undefined && token !== null && token !== '') {
      try {
        const payload = JSON.parse(atob(token.split('.')[1]));
        return Array.isArray(payload.roles) === true ? payload.roles : [];
      } catch (e1) {}
    }
    const userDataCookie = document.cookie.split('; ').find(row => row.startsWith('userData=') !== false);
    if (userDataCookie !== undefined) {
      const cookieValue = userDataCookie.split('=')[1];
      if (cookieValue !== undefined && cookieValue !== null && cookieValue !== '') {
        try {
          const decodedValue = decodeURIComponent(cookieValue);
          if (decodedValue !== 'undefined') {
            const userData = JSON.parse(decodedValue);
            if (userData !== null && Array.isArray(userData.roles) === true) {
              return userData.roles;
            }
          }
        } catch (e2) {}
      }
    }
  } catch (e) {}
  return [];
}

function isAgentEditorRoleActive() {
  const roles = getUserRolesFromCookies();
  return roles.some(r => typeof r === 'string' && r.trim().toLowerCase() === 'agent editor') === true;
}

let isActive = true;
const isAgentModalOpen = ref(false);
const isEditingModal = ref(false);
const isDeleteModalOpen = ref(false);
const isProcessingDeleteModalOpen = ref(false);
const isKnowledgeStatusModalOpen = ref(false);
const isKnowledgeSearchModalOpen = ref(false);
const showOpenAgentModal = ref(false);
const openAgentModalChannels = ref([]);
const currentOpenAgent = ref(null);
const openAgentBindingsByAgentId = reactive({});
const isSearchingKnowledgeByLocation = ref(false);
const knowledgeSearchLocationInput = ref('');
const knowledgeSearchResults = ref([]);
const knowledgeSearchError = ref('');
const showScrapeReportModal = ref(false);
const scrapeReportSource = ref(null);
const currentAgentForStatus = ref(null);
const knowledgeStatusData = ref([]);
const isLoadingKnowledgeStatus = ref(false);
const isLoadingAgents = ref(false);
const itemsPerPageStatus = 5;
const currentStatusPage = ref(1);
const statusPageInput = ref('1');
const columnFiltersModal = reactive({ name: '', type: '', status: '', reason: '' })
const uniqueTypes = computed(() => [...new Set(knowledgeStatusData.value.map(item => item.type))])
const hiddenKnowledgeStatusReason = 'File not found in storage. It might be pending upload or an error occurred before processing.'
const statusFilterOptions = computed(() => [
  { value: 'failed', label: t('aiChats.knowledgeStatusModal.statusFilterFailed', 'Failed') },
  { value: 'error', label: t('aiChats.knowledgeStatusModal.statusFilterError', 'Error') },
  { value: 'available', label: t('aiChats.knowledgeStatusModal.statusFilterAvailable', 'Available') }
]);

function humanizeType(type) {
  if (type === 'websitepage') return 'Full Website';
  if (type === 'websiteurl') return 'Website URL';
  if (type === 'website') return 'Website';
  if (/^(xlsx|pdf|docx)$/i.test(type)!==false) return type.toUpperCase();
  return type.charAt(0).toUpperCase() + type.slice(1);
}

function getVisibleKnowledgeStatusReason(details) {
  const text = typeof details === 'string' ? details : '';
  if (text === hiddenKnowledgeStatusReason) {
    return '';
  }
  return text;
}

function setStatusPage(pageValue) {
  const numericPage = Number(pageValue);
  let nextPage = Number.isFinite(numericPage) === true ? Math.floor(numericPage) : 1;
  if (nextPage < 1) {
    nextPage = 1;
  }
  if (nextPage > safeTotalStatusPages.value) {
    nextPage = safeTotalStatusPages.value;
  }
  currentStatusPage.value = nextPage;
  statusPageInput.value = String(nextPage);
}

function jumpToStatusPage() {
  setStatusPage(statusPageInput.value);
}

function matchesKnowledgeStatusFilter(itemStatus, displayStatus, statusFilter) {
  if (statusFilter === '') {
    return true;
  }
  const normalizedDisplayStatus = typeof displayStatus === 'string' ? displayStatus.trim().toLowerCase() : '';
  const normalizedItemStatus = typeof itemStatus === 'string' ? itemStatus.trim().toLowerCase() : '';
  if (statusFilter === 'available') {
    return normalizedDisplayStatus === 'available';
  }
  if (statusFilter === 'failed') {
    return normalizedItemStatus === 'failed';
  }
  if (statusFilter === 'error') {
    return normalizedItemStatus === 'error' || normalizedItemStatus === 'pending / error';
  }
  return normalizedDisplayStatus === statusFilter || normalizedItemStatus === statusFilter;
}

const typeOptions = computed(() => uniqueTypes.value.map(t => ({ value: t, label: humanizeType(t) })));

const filteredKnowledgeStatusData = computed(() => {
  return knowledgeStatusData.value.filter(item => {
    const matchesName = columnFiltersModal.name === '' || (item.name.toLowerCase().includes(columnFiltersModal.name.toLowerCase()) === true)
    const matchesType = columnFiltersModal.type === '' || (item.type === columnFiltersModal.type)
    const displayStatus = (item.status === 'Processed' || item.status === 'Completed') ? 'Available' : item.status;
    const normalizedStatusFilter = typeof columnFiltersModal.status === 'string' ? columnFiltersModal.status.trim().toLowerCase() : '';
    const matchesStatus = matchesKnowledgeStatusFilter(item.status, displayStatus, normalizedStatusFilter);
    const visibleReason = getVisibleKnowledgeStatusReason(item.details);
    const matchesReason = columnFiltersModal.reason === '' || (visibleReason.toLowerCase().includes(columnFiltersModal.reason.toLowerCase()) === true)
    return (matchesName === true) && (matchesType === true) && (matchesStatus === true) && (matchesReason === true)
  })
});
const totalStatusPages = computed(() => Math.ceil(filteredKnowledgeStatusData.value.length / itemsPerPageStatus));
const safeTotalStatusPages = computed(() => totalStatusPages.value > 0 ? totalStatusPages.value : 1);
const paginatedKnowledgeStatusData = computed(() => {
  const startIndex = (currentStatusPage.value - 1) * itemsPerPageStatus;
  const endIndex = currentStatusPage.value * itemsPerPageStatus;
  return filteredKnowledgeStatusData.value.slice(startIndex, endIndex);
})
const knowledgeSummary = computed(() => {
  const data = Array.isArray(knowledgeStatusData.value) === true ? knowledgeStatusData.value : [];
  const total = data.length;
  let available = 0;
  let processing = 0;
  let issues = 0;
  const availableStatuses = ['Processed','Completed'];
  const processingStatuses = ['Processing','Scraping In Progress','Discovering Links'];
  const issueStatuses = ['Error','Failed','Pending / Error','PartiallyCompleted'];
  for (let i = 0; i < data.length; i = i + 1) {
    const st = typeof data[i].status === 'string' ? data[i].status : '';
    if (availableStatuses.includes(st) === true) {
      available = available + 1;
    } else if (processingStatuses.includes(st) === true) {
      processing = processing + 1;
    } else if (issueStatuses.includes(st) === true) {
      issues = issues + 1;
    }
  }
  return { total, available, processing, issues };
})
const searchFields = ["rowNumber", "agentname", "agentstatus","agenttype"];
const searchValue = ref("");
const agentName = ref("");
const agentDescription = ref("");
const apiEndpoint = reactive(globalState.apiEndpoint);
const isCreating = ref(false);
const isEditing = ref(null);

const rawHeaders = [
  { text: "ID", value: "rowNumber", sortable: true, i18nKey: "aiChats.table.headerId" },
  { text: "Agent Name", value: "agentname", sortable: true, i18nKey: "aiChats.table.headerAgentName" },
  { text: "Status", value: "agentstatus", sortable: true, i18nKey: "aiChats.table.headerStatus" },
  { text: "Channels", value: "channels", i18nKey: "aiChats.table.headerChannels" },
  { text: "Type", value: "agenttype", sortable: true, i18nKey: "aiChats.table.headerType" },
  { text: "Options", value: "options", i18nKey: "aiChats.table.headerOptions" }
];

const translatedHeaders = computed(() => {
    return rawHeaders.map(header => ({
        ...header,
        text: t(header.i18nKey, header.text) 
    }));
});

let aiAgents = ref([]);
let selectedAgentId = ref(null);

const isLimitReachedModalOpen = ref(false);
const isCreateOptionsModalOpen = ref(false);
const isManualModeOptionsOpen = ref(false);
const manualStepDirection = ref('forward');
const isImportExistingModalOpen = ref(false);
const importTargetAgentId = ref('');
const importExistingFile = ref(null);
const importExistingFileName = ref('');
const importExistingError = ref('');
const importExistingSuccess = ref('');
const isImportingExistingAgent = ref(false);
const limitAgentSetupOptionRaw = process.env.VUE_APP_LIMIT_AGENT_SETUP_OPTION;
const limitAgentSetupOption = limitAgentSetupOptionRaw !== undefined ? limitAgentSetupOptionRaw.toLowerCase() : null;
const chosenSetupMethodRaw = process.env.VUE_APP_CHOSEN_SETUP_METHOD;
const chosenSetupMethod = chosenSetupMethodRaw !== undefined ? chosenSetupMethodRaw.toLowerCase() : null;

const openCreateOptions = () => {
  selectedMode.value = null;
  manualStepDirection.value = 'forward';
  isManualModeOptionsOpen.value = false;
  isCreateOptionsModalOpen.value = true;
};

const handleCreateClick = () => {
  if (isAgentEditorRoleActive() === true) {
    openCreateOptions();
    return;
  }
  if (chosenSetupMethod === 'standard') {
    selectCreateMode('standard');
  } else if (limitAgentSetupOption === 'light' || limitAgentSetupOption === 'standard' || limitAgentSetupOption === 'automatic' || limitAgentSetupOption === 'ivr') {
    selectCreateMode(limitAgentSetupOption);
  } else {
    openCreateOptions();
  }
};
const toggleCreateDropdown = () => {
  isCreateDropdownOpen.value = isCreateDropdownOpen.value !== true;
};
const onCreateDropdownSelect = (mode) => {
  isCreateDropdownOpen.value = false;
  if (mode === 'import') {
    openImportExistingModal();
    return;
  }
  handleCreateClick();
};
const openImportExistingModal = () => {
  importExistingError.value = '';
  importExistingSuccess.value = '';
  importExistingFile.value = null;
  importExistingFileName.value = '';
  if (importTargetAgentId.value.length === 0 && aiAgents.value.length > 0) {
    importTargetAgentId.value = String(aiAgents.value[0].agentid);
  }
  isImportExistingModalOpen.value = true;
};
const closeImportExistingModal = () => {
  if (isImportingExistingAgent.value === true) {
    return;
  }
  isImportExistingModalOpen.value = false;
};
const onImportExistingFileChange = (event) => {
  const target = event !== undefined && event !== null ? event.target : null;
  const files = target !== null && target !== undefined ? target.files : null;
  if (files === null || files === undefined || files.length === 0) {
    importExistingFile.value = null;
    importExistingFileName.value = '';
    return;
  }
  importExistingFile.value = files[0];
  importExistingFileName.value = typeof files[0].name === 'string' ? files[0].name : '';
};
const readFileAsDataUrl = (fileObject) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(typeof reader.result === 'string' ? reader.result : '');
    reader.onerror = () => reject(new Error('Failed to read file.'));
    reader.readAsDataURL(fileObject);
  });
};
const submitImportExistingAgent = async () => {
  if (isImportingExistingAgent.value === true) {
    return;
  }
  if (importTargetAgentId.value.length === 0) {
    importExistingError.value = 'Please select an agent.';
    return;
  }
  if (importExistingFile.value === null) {
    importExistingError.value = 'Please select a zip file.';
    return;
  }
  importExistingError.value = '';
  importExistingSuccess.value = '';
  isImportingExistingAgent.value = true;
  try {
    const zipBase64 = await readFileAsDataUrl(importExistingFile.value);
    const safeAgentId = encodeURIComponent(String(importTargetAgentId.value));
    await axios.post(`${apiEndpoint}/api/agents/${safeAgentId}/import`, { zipBase64 }, { headers: getAuthHeaders() });
    importExistingSuccess.value = 'Agent imported successfully.';
    await loadAIAgents({ force: true });
  } catch (error) {
    console.error('Error importing existing agent:', error);
    importExistingError.value = typeof error?.response?.data?.message === 'string' ? error.response.data.message : 'Failed to import agent.';
    handleAuthError(error);
  } finally {
    isImportingExistingAgent.value = false;
  }
};
const onGlobalClickCloseCreateDropdown = (event) => {
  if (isCreateDropdownOpen.value !== true) {
    return;
  }
  const root = createDropdownRef.value;
  if (root === null || root === undefined) {
    isCreateDropdownOpen.value = false;
    return;
  }
  if (root.contains(event.target) === false) {
    isCreateDropdownOpen.value = false;
  }
};
const showProcessingModal = ref(false);

const columnFilters = reactive({ agentname: '', agentstatus: '', agenttype: '' });
const activeFilterColumn = ref('');

function stripControlChars(value) {
  if (typeof value !== 'string') {
    return '';
  }
  let out = '';
  for (let i = 0; i < value.length; i = i + 1) {
    const code = value.charCodeAt(i);
    if (code >= 32 && code !== 127) {
      out = out + value[i];
    }
  }
  return out;
}

function sanitizeTextValue(value, maxLen) {
  const raw = typeof value === 'string' ? value : '';
  let cleaned = stripControlChars(raw).trim().replace(/\s+/g, ' ');
  if (typeof maxLen === 'number' && maxLen >= 1 && cleaned.length > maxLen) {
    cleaned = cleaned.slice(0, maxLen);
  }
  return cleaned;
}

function sanitizeUrlValue(value) {
  const raw = typeof value === 'string' ? value : '';
  const cleaned = stripControlChars(raw).trim();
  if (cleaned === '') {
    return '';
  }
  if (cleaned.length > urlMaxLength) {
    return '';
  }
  let parsed = null;
  try {
    parsed = new URL(cleaned);
  } catch (e) {
    parsed = null;
  }
  if (parsed === null) {
    return '';
  }
  const protocol = typeof parsed.protocol === 'string' ? parsed.protocol.toLowerCase() : '';
  if (protocol === 'http:' || protocol === 'https:') {
    return parsed.toString();
  }
  return '';
}

watch(searchValue, (nextValue) => {
  const sanitized = sanitizeTextValue(nextValue, searchMaxLength);
  if (sanitized !== nextValue) {
    searchValue.value = sanitized;
  }
});

watch(() => columnFilters.agentname, (nextValue) => {
  const sanitized = sanitizeTextValue(nextValue, columnFilterMaxLength);
  if (sanitized !== nextValue) {
    columnFilters.agentname = sanitized;
  }
});

watch(() => columnFilters.agentstatus, (nextValue) => {
  const sanitized = sanitizeTextValue(nextValue, columnFilterMaxLength);
  if (sanitized !== nextValue) {
    columnFilters.agentstatus = sanitized;
  }
});

watch(() => columnFilters.agenttype, (nextValue) => {
  const sanitized = sanitizeTextValue(nextValue, columnFilterMaxLength);
  if (sanitized !== nextValue) {
    columnFilters.agenttype = sanitized;
  }
});

watch(() => columnFiltersModal.name, (nextValue) => {
  const sanitized = sanitizeTextValue(nextValue, modalFilterMaxLength);
  if (sanitized !== nextValue) {
    columnFiltersModal.name = sanitized;
    return;
  }
  setStatusPage(1);
});

watch(() => columnFiltersModal.type, () => {
  setStatusPage(1);
});

watch(() => columnFiltersModal.status, (nextValue) => {
  const sanitized = sanitizeTextValue(nextValue, modalFilterMaxLength);
  if (sanitized !== nextValue) {
    columnFiltersModal.status = sanitized;
    return;
  }
  setStatusPage(1);
});

watch(() => columnFiltersModal.reason, (nextValue) => {
  const sanitized = sanitizeTextValue(nextValue, modalFilterMaxLength);
  if (sanitized !== nextValue) {
    columnFiltersModal.reason = sanitized;
    return;
  }
  setStatusPage(1);
});

watch(currentStatusPage, (nextValue) => {
  statusPageInput.value = String(nextValue);
});

watch(totalStatusPages, () => {
  setStatusPage(currentStatusPage.value);
});

watch(knowledgeSearchLocationInput, (nextValue) => {
  const sanitized = sanitizeTextValue(nextValue, modalFilterMaxLength);
  if (sanitized !== nextValue) {
    knowledgeSearchLocationInput.value = sanitized;
  }
});

watch(agentName, (nextValue) => {
  const sanitized = sanitizeTextValue(nextValue, 30);
  if (sanitized !== nextValue) {
    agentName.value = sanitized;
  }
});

watch(agentDescription, (nextValue) => {
  const sanitized = sanitizeTextValue(nextValue, 30);
  if (sanitized !== nextValue) {
    agentDescription.value = sanitized;
  }
});

const filteredAiAgents = computed(() => {
    return aiAgents.value.filter(a => (columnFilters.agentname === '' || a.agentname.toLowerCase().includes(columnFilters.agentname.toLowerCase()) === true) && (columnFilters.agentstatus === '' || a.agentstatus.toLowerCase().includes(columnFilters.agentstatus.toLowerCase()) === true) && (columnFilters.agenttype === '' || a.agenttype.toLowerCase().includes(columnFilters.agenttype.toLowerCase()) === true));
  });
function toggleFilterColumn(col) {
  if (activeFilterColumn.value === col) {
    activeFilterColumn.value = '';
  } else {
    activeFilterColumn.value = col;
  }
}

const selectedMode = ref(null);
const openManualModeOptions = () => {
  selectedMode.value = null;
  manualStepDirection.value = 'forward';
  isManualModeOptionsOpen.value = true;
};
const handleCancel = () => {
  selectedMode.value = null;
  if (isManualModeOptionsOpen.value === true) {
    manualStepDirection.value = 'back';
    isManualModeOptionsOpen.value = false;
    return;
  }
  isCreateOptionsModalOpen.value = false;
};
const handleContinue = () => { if (selectedMode.value !== null) selectCreateMode(selectedMode.value); };

const isDeployModalOpen = ref(false);
const currentAgentForDeploy = ref(null);
function openDeployModal(rowNumber) {
  if (rowNumber !== null) {
    const index = rowNumber - 1;
    if (index >= 0 && index < aiAgents.value.length) {
      currentAgentForDeploy.value = { ...aiAgents.value[index] };
      isDeployModalOpen.value = true;
    }
  }
}

const viewMode = ref('table');
const currentGridPage = ref(1);
const gridPageSize = 20;
const agentIdToIcon = reactive({});

const gridFilteredAgents = computed(() => {
  const term = typeof searchValue.value === 'string' ? searchValue.value.toLowerCase() : '';
  return aiAgents.value.filter(a => {
    const passesColumns = (columnFilters.agentname === '' || (typeof a.agentname === 'string' && a.agentname.toLowerCase().includes(columnFilters.agentname.toLowerCase()) === true)) && (columnFilters.agentstatus === '' || (typeof a.agentstatus === 'string' && a.agentstatus.toLowerCase().includes(columnFilters.agentstatus.toLowerCase()) === true)) && (columnFilters.agenttype === '' || (typeof a.agenttype === 'string' && a.agenttype.toLowerCase().includes(columnFilters.agenttype.toLowerCase()) === true));
    if (passesColumns === false) return false;
    if (term === '') return true;
    const n = typeof a.agentname === 'string' ? a.agentname.toLowerCase() : '';
    const s = typeof a.agentstatus === 'string' ? a.agentstatus.toLowerCase() : '';
    const t = typeof a.agenttype === 'string' ? a.agenttype.toLowerCase() : '';
    return (n.includes(term) === true) || (s.includes(term) === true) || (t.includes(term) === true);
  });
});

const totalGridPagesGrid = computed(() => {
  const total = gridFilteredAgents.value.length;
  const pages = Math.ceil(total / gridPageSize);
  return pages > 0 ? pages : 1;
});

const paginatedGridAgents = computed(() => {
  const startIndex = (currentGridPage.value - 1) * gridPageSize;
  const endIndex = currentGridPage.value * gridPageSize;
  return gridFilteredAgents.value.slice(startIndex, endIndex);
});

function normalizeStyleGuid(agent) {
  if (agent.styleguid !== undefined && agent.styleguid !== null && agent.styleguid !== '') {
    return agent.styleguid;
  }
  if (agent.styleGuid !== undefined && agent.styleGuid !== null && agent.styleGuid !== '') {
    return agent.styleGuid;
  }
  if (agent.agentdata !== undefined && agent.agentdata !== null && agent.agentdata.styleGuid !== undefined && agent.agentdata.styleGuid !== null && agent.agentdata.styleGuid !== '') {
    return agent.agentdata.styleGuid;
  }
  return '';
}

function updateAgentIcons(data) {
  const nextLogos = {};
  for (const a of data) {
    const guid = normalizeStyleGuid(a);
    const agentId = a.agentid === undefined ? a.agentId : a.agentid;
    if (guid !== '') {
      const style = a.agentStyle === undefined ? a.agentstyle : a.agentStyle;
      const logoBase64 = style !== undefined && style !== null && typeof style.logoBase64 === 'string' ? style.logoBase64 : (style !== undefined && style !== null && typeof style.logobase64 === 'string' ? style.logobase64 : '');
      if (logoBase64 !== '') {
        const url = logoBase64.startsWith('data:image') === true ? logoBase64 : `data:image/png;base64,${logoBase64}`;
        nextLogos[agentId] = url;
      }
    }
  }
  Object.assign(agentIdToIcon, nextLogos);
}

function getShortDescription(item) {
  const sd = item.agentShortDescription !== undefined && item.agentShortDescription !== null && String(item.agentShortDescription).length > 0 ? String(item.agentShortDescription) : (item.agentshortdescription !== undefined && item.agentshortdescription !== null && String(item.agentshortdescription).length > 0 ? String(item.agentshortdescription) : (item.agentDescription !== undefined && item.agentDescription !== null && String(item.agentDescription).length > 0 ? String(item.agentDescription) : (item.agentdescription !== undefined && item.agentdescription !== null ? String(item.agentdescription) : '')));
  if (sd.length > 200) {
    return sd.slice(0, 200) + '...';
  }
  return sd;
}

function getAgentIcon(agentId) {
  const ic = agentIdToIcon[agentId];
  if (ic === undefined) return null;
  return ic;
}

function getClassicAgentModeLabel(item) {
  const setupType = item !== null && item !== undefined && item.agentdata !== undefined && item.agentdata !== null ? item.agentdata.setupType : '';
  if (setupType === 'light') {
    return t('aiChats.lightMode', 'Light');
  }
  return t('aiChats.standardMode', 'Standard');
}

function resolveAgentId(item) {
  if (item.agentid !== undefined && item.agentid !== null) {
    return item.agentid;
  }
  if (item.agentId !== undefined && item.agentId !== null) {
    return item.agentId;
  }
  return item.rowNumber;
}

function getAgentByRowNumber(rowNumber) {
  if (rowNumber === null || rowNumber === undefined) {
    return null;
  }
  const index = Number(rowNumber) - 1;
  if (Number.isInteger(index) !== true || index < 0 || index >= aiAgents.value.length) {
    return null;
  }
  return aiAgents.value[index];
}

function getConfiguredDeploymentChannels(agent) {
  if (agent !== null && agent !== undefined && Array.isArray(agent.availableDeploymentChannels) === true) {
    return normalizeOpenAgentSelectedChannels(agent.availableDeploymentChannels);
  }
  const configured = agent !== null && agent !== undefined && agent.agentdata !== undefined && agent.agentdata !== null && agent.agentdata.configurations !== undefined && agent.agentdata.configurations !== null
    ? agent.agentdata.configurations.deploymentChannels
    : [];
  return normalizeOpenAgentSelectedChannels(configured);
}

function getChannelIconConfig(channelId) {
  if (channelId === 'Web') {
    return { id: channelId, icon: 'heroicons:globe-alt', label: t('deploymentChannels.channels.web.name', 'Web') };
  }
  if (channelId === 'Web Voice Agent') {
    return { id: channelId, icon: 'tabler:microphone', label: t('deploymentChannels.channels.webVoiceAgent.name', 'Web Voice Agent') };
  }
  if (channelId === 'Whatsapp') {
    return { id: channelId, icon: 'tabler:brand-whatsapp', label: t('deploymentChannels.channels.whatsapp.name', 'Whatsapp') };
  }
  if (channelId === 'Whatsapp Voice') {
    return { id: channelId, icon: 'tabler:brand-whatsapp', label: t('deploymentChannels.channels.whatsappVoice.name', 'WhatsApp Voice') };
  }
  if (channelId === 'Phone') {
    return { id: channelId, icon: 'tabler:phone', label: t('deploymentChannels.channels.phone.name', 'Phone') };
  }
  if (channelId === 'Email') {
    return { id: channelId, icon: 'tabler:mail', label: t('deploymentChannels.channels.email.name', 'Email') };
  }
  return null;
}

function getAvailableChannelDescriptorsForAgent(agent) {
  const configuredChannels = getConfiguredDeploymentChannels(agent);
  const descriptors = [];
  for (let index = 0; index < configuredChannels.length; index = index + 1) {
    const nextDescriptor = getChannelIconConfig(configuredChannels[index]);
    if (nextDescriptor !== null) {
      descriptors.push(nextDescriptor);
    }
  }
  return descriptors;
}

function resolveAvailableChannelsForRow(item, rowNumber) {
  if (item !== null && item !== undefined) {
    return getAvailableChannelDescriptorsForAgent(item);
  }
  return getAvailableChannelDescriptorsForAgent(getAgentByRowNumber(rowNumber));
}

function getAgentModelLabel(agent) {
  const candidate = agent !== null && agent !== undefined && agent.agentdata !== undefined && agent.agentdata !== null && typeof agent.agentdata.selectedLlmModelId === 'string' && agent.agentdata.selectedLlmModelId.trim() !== ''
    ? agent.agentdata.selectedLlmModelId.trim()
    : (agent !== null && agent !== undefined && typeof agent.selectedLlmModelId === 'string' && agent.selectedLlmModelId.trim() !== ''
      ? agent.selectedLlmModelId.trim()
      : (agent !== null && agent !== undefined && agent.agentdata !== undefined && agent.agentdata !== null && typeof agent.agentdata.llmModelId === 'string' && agent.agentdata.llmModelId.trim() !== ''
        ? agent.agentdata.llmModelId.trim()
        : (agent !== null && agent !== undefined && typeof agent.llmModelId === 'string' && agent.llmModelId.trim() !== ''
          ? agent.llmModelId.trim()
          : '')));
  if (candidate === 'auto-llm' || candidate === 'auto') {
    return t('aiChatAgentSetup.agentInformation.autoLlmModel', 'Auto');
  }
  return candidate;
}

function getOpenAgentBindingsCacheKey(agent) {
  const agentId = agent !== null && agent !== undefined ? resolveAgentId(agent) : '';
  return agentId !== undefined && agentId !== null ? String(agentId) : '';
}

function extractBindingDisplayNumber(data) {
  if (data !== null && data !== undefined && typeof data.number === 'string' && data.number.trim() !== '') {
    return data.number.trim();
  }
  if (data !== null && data !== undefined && typeof data.externalNumber === 'string' && data.externalNumber.trim() !== '') {
    return data.externalNumber.trim();
  }
  if (data !== null && data !== undefined && typeof data.display === 'string' && data.display.trim() !== '') {
    return data.display.trim();
  }
  return '';
}

function getCachedOpenAgentBindings(agent) {
  const cacheKey = getOpenAgentBindingsCacheKey(agent);
  if (cacheKey === '') {
    return { whatsappDisplayNumber: '', phoneDisplayNumber: '' };
  }
  if (openAgentBindingsByAgentId[cacheKey] !== undefined) {
    return openAgentBindingsByAgentId[cacheKey];
  }
  return { whatsappDisplayNumber: '', phoneDisplayNumber: '' };
}

function getAvailableOpenAgentChannelsForAgent(agent) {
  const selectedChannels = getConfiguredDeploymentChannels(agent);
  const cachedBindings = getCachedOpenAgentBindings(agent);
  return buildOpenAgentChannels({
    selectedChannels,
    whatsappNumber: cachedBindings.whatsappDisplayNumber,
    phoneNumber: cachedBindings.phoneDisplayNumber,
    t
  });
}

function translateAiChatsOpenLabel(key, fallback) {
  if (key === 'aiChatAgentSetup.buttons.openAgent') {
    return t('aiChats.openAgentButton', 'Open Agent');
  }
  return t(key, fallback);
}

function resolveOpenButtonLabelForAgent(agent) {
  if (agent === null || agent === undefined) {
    return t('aiChats.openAgentButton', 'Open Agent');
  }
  const availableChannels = getAvailableOpenAgentChannelsForAgent(agent);
  if (availableChannels.length > 0) {
    return getOpenAgentButtonLabel(availableChannels, translateAiChatsOpenLabel);
  }
  const configuredChannels = getConfiguredDeploymentChannels(agent);
  if (configuredChannels.length === 1 && configuredChannels[0] === 'Web') {
    return t('aiChatAgentSetup.buttons.openChat', 'Open Chat');
  }
  return t('aiChats.openAgentButton', 'Open Agent');
}

function resolveOpenButtonLabelByRow(rowNumber) {
  return resolveOpenButtonLabelForAgent(getAgentByRowNumber(rowNumber));
}

function getGridStatusClass(item) {
  const status = typeof item.agentstatus === 'string' ? item.agentstatus.toLowerCase() : '';
  if (status === 'active') {
    return 'status-active';
  }
  return 'status-inactive';
}

function getGridStatusLabel(item) {
  const status = typeof item.agentstatus === 'string' ? item.agentstatus.toLowerCase() : '';
  if (status === 'active') {
    return t('aiChats.statusActive', 'Active');
  }
  return t('aiChats.statusDisabled', 'Disabled');
}

function getColorForAgent(item) {
  const id = item.agentid !== undefined && item.agentid !== null ? String(item.agentid) : (item.agentId !== undefined && item.agentId !== null ? String(item.agentId) : String(item.agentname));
  let hash = 0;
  for (let i = 0; i < id.length; i = i + 1) {
    hash = ((hash << 5) - hash) + id.charCodeAt(i);
    hash = hash & hash;
  }
  const hue = Math.abs(hash) % 360;
  return `hsl(${hue}, 65%, 55%)`;
}

watch(viewMode, async (val) => {
  currentGridPage.value = 1;
  await nextTick();
});

watch([searchValue, () => columnFilters.agentname, () => columnFilters.agentstatus, () => columnFilters.agenttype], async () => {
  currentGridPage.value = 1;
  await nextTick();
});

onMounted(async () => {
  if (route.query.processing === 'true') {
    showProcessingModal.value = true;
  }
  await loadAIAgents();
  loadStyle(`
.vue3-easy-data-table {
  height: 95%;
}
 
.sp-trigger-container.sp-opened{
  box-shadow:none;
}
  .agentStatus {
  padding: 0.25rem 0.75rem;
  background: #dcfce7;
  color: #166534;
  border-radius: 9999px;
  font-size: 0.875rem;
  font-weight: 500;
}
  .dp__main{
    width:262px;
    height:100%;
  }
    .inactiveAgent{
    --tw-bg-opacity: 0.4;
    background-color: rgb(239 68 68 / var(--tw-bg-opacity)) !important;
    color:white;
    }
    .sp-trigger-container .sp-trigger.sp-chips{
    max-height: 1.8rem;
    overflow: auto;
    }
    .sp-trigger-container{
      height:3rem;
    }
    .dp__main input {
     padding-left:17%;
    border-color: #dadcdf;
    flex-shrink: 1;
    appearance: none;
    height: 3rem;
    width:260px;
    font-size: 1rem;
    line-height: 2;
    line-height: 1.5rem;
    border-radius: var(--rounded-btn, .5rem);
    border-width: 1px;
    
    --tw-bg-opacity: 1;
    background-color: var(--fallback-b1,oklch(var(--b1)/var(--tw-bg-opacity)));
    }

    .multiselect{
      height:3rem;
      width: 180px;
      margin-right:1%;
    }

    .modern-pagination-btn {
      width: 36px;
      height: 36px;
      border-radius: 12px;
      display: flex;
      align-items: center;
      justify-content: center;
      background-color: #ffffff;
      border: 1px solid #f1f5f9;
      color: #64748b;
      transition: all 0.2s ease;
      cursor: pointer;
    }

    .modern-pagination-btn:hover:not(:disabled) {
      background-color: #f8fafc;
      border-color: #e2e8f0;
      color: #0f172a;
      transform: translateY(-1px);
    }

    .modern-pagination-btn:disabled {
      opacity: 0.5;
      cursor: not-allowed;
      background-color: #f1f5f9;
    }

    .dark .modern-pagination-btn {
      background-color: #1e293b;
      border-color: #334155;
      color: #94a3b8;
    }

    .dark .modern-pagination-btn:hover:not(:disabled) {
      background-color: #334155;
      color: #f8fafc;
    }
  `);

  await nextTick();
  watch(currentLanguage, async () => {
    if (isActive === false) {
      return;
    }
    await nextTick();
  });
  window.addEventListener('click', onGlobalClickCloseCreateDropdown, true);
});

onBeforeUnmount(() => {
  isActive = false;
  window.removeEventListener('click', onGlobalClickCloseCreateDropdown, true);
});

const handleDeleteClick = (rowNumber) => {
  if (rowNumber !== null) {
    const index = rowNumber - 1;
    if (index >= 0 && index < aiAgents.value.length) {
      selectedAgentId.value = aiAgents.value[index].agentid;
      isDeleteModalOpen.value = true;
    }
  }
};

const getAuthToken = () => {
  const tokenCookie = document.cookie.split('; ').find(row => row.startsWith('authToken=') !== false);
  if (tokenCookie !== undefined) {
    return tokenCookie.split('=')[1];
  }
  const jwtCookie = document.cookie.split('; ').find(row => row.startsWith('jwtToken=') !== false);
  if (jwtCookie !== undefined) {
    return jwtCookie.split('=')[1];
  }
  return null;
};

const getAuthHeaders = () => {
  const token = getAuthToken();
  if (token === null) {
    return {};
  }
  return {
    'Authorization': `Bearer ${token}`,
    'Content-Type': 'application/json'
  };
};

const handleAuthError = (error) => {
  const message = typeof error?.response?.data?.message === 'string' ? error.response.data.message : (typeof error?.response?.data?.error === 'string' ? error.response.data.error : '');
  const expired = typeof message === 'string' ? message.toLowerCase().includes('jwt expired') : false;
  if (error.response?.status === 401 || error.response?.status === 403 || expired === true) {
    router.push('/login');
  }
};

const deleteAgent = async (agentId) => {
  try {
    const safeAgentId = encodeURIComponent(String(agentId));
    await axios.delete(`${apiEndpoint}/deleteAIChat/${safeAgentId}`, {
      headers: getAuthHeaders()
    });
    await loadAIAgents();
    isDeleteModalOpen.value = false;
    selectedAgentId.value = null;
  } catch (error) {
    console.error('Error deleting agent:', error);
    if (error.response !== undefined && error.response !== null && error.response.status === 500 && (error.response.data?.error?.includes('Azure AI Search deleteMany requires "filterExpression" to be set in the filter object') === true)) {
      isDeleteModalOpen.value = false; 
      isProcessingDeleteModalOpen.value = true; 
    } else {
      handleAuthError(error); 
    }
  }
};

let loadAgentsInFlight = null;
const loadAIAgents = async (options = {}) => {
  const force = options.force === true;
  if (loadAgentsInFlight !== null && force === false) {
    return loadAgentsInFlight;
  }
  isLoadingAgents.value = true;
  loadAgentsInFlight = (async () => {
    try {
      const data = await getAllAIChats({ includeStyles: true, force });
      const items = Array.isArray(data) === true ? data : [];
      aiAgents.value = items.map((item, index) => ({
        ...item,
        modelLabel: getAgentModelLabel(item),
        rowNumber: index + 1
      }));
      updateAgentIcons(items);
    } catch (error) {
      console.error('Error loading agents:', error);
      handleAuthError(error);
    } finally {
      isLoadingAgents.value = false;
      loadAgentsInFlight = null;
    }
  })();
  return loadAgentsInFlight;
};

function openAgentTarget(target) {
  if (target === null) {
    return;
  }
  closeOpenAgentModal();
  if (target.mode === 'window') {
    window.open(target.url, '_blank', 'noopener,noreferrer');
    return;
  }
  router.push(target.url);
}

function openWebAgentChannelForAgent(agent) {
  openAgentTarget(resolveOpenWebAgentTarget(agent));
}

function openWebVoiceAgentChannelForAgent(agent) {
  openAgentTarget(resolveOpenWebVoiceAgentTarget(agent));
}

function openPhoneAgentNumber(value) {
  const normalized = normalizeOpenAgentTelValue(value);
  if (normalized.length === 0) {
    toast.error(t('aiChatAgentSetup.openAgent.noAvailableChannels', 'No openable deployment channel is connected for this agent.'));
    return;
  }
  closeOpenAgentModal();
  window.location.href = `tel:${normalized}`;
}

function openWhatsappAgentNumber(value) {
  const digits = normalizeOpenAgentPhoneDigits(value);
  if (digits.length === 0) {
    toast.error(t('aiChatAgentSetup.openAgent.missingWhatsappNumber', 'Cannot open WhatsApp: no number is connected.'));
    return;
  }
  closeOpenAgentModal();
  try {
    const safe = new URL(`https://web.whatsapp.com/send?phone=${encodeURIComponent(digits)}`);
    window.open(safe.toString(), '_blank', 'noopener,noreferrer');
  } catch (error) {
  }
}

function closeOpenAgentModal() {
  showOpenAgentModal.value = false;
  openAgentModalChannels.value = [];
  currentOpenAgent.value = null;
}

async function ensureOpenAgentBindings(agent) {
  const cacheKey = getOpenAgentBindingsCacheKey(agent);
  if (cacheKey === '') {
    return { whatsappDisplayNumber: '', phoneDisplayNumber: '' };
  }
  if (openAgentBindingsByAgentId[cacheKey] !== undefined) {
    return openAgentBindingsByAgentId[cacheKey];
  }
  const selectedChannels = getConfiguredDeploymentChannels(agent);
  const nextBindings = { whatsappDisplayNumber: '', phoneDisplayNumber: '' };
  const requests = [];
  if (selectedChannels.includes('Whatsapp') === true || selectedChannels.includes('Whatsapp Voice') === true) {
    requests.push(
      axios.get(`${apiEndpoint}/api/whatsapp/binding/${encodeURIComponent(cacheKey)}`, { headers: getAuthHeaders() }).then(response => {
        nextBindings.whatsappDisplayNumber = extractBindingDisplayNumber(response !== null && response !== undefined ? response.data : null);
      }).catch(error => {
        handleAuthError(error);
      })
    );
  }
  if (selectedChannels.includes('Phone') === true) {
    requests.push(
      axios.get(`${apiEndpoint}/api/phone/binding/${encodeURIComponent(cacheKey)}`, { headers: getAuthHeaders() }).then(response => {
        nextBindings.phoneDisplayNumber = extractBindingDisplayNumber(response !== null && response !== undefined ? response.data : null);
      }).catch(error => {
        handleAuthError(error);
      })
    );
  }
  if (requests.length > 0) {
    await Promise.all(requests);
  }
  openAgentBindingsByAgentId[cacheKey] = nextBindings;
  return nextBindings;
}

function openSelectedAgentChannel(channelId) {
  if (currentOpenAgent.value === null || currentOpenAgent.value === undefined) {
    return;
  }
  if (channelId === 'Web') {
    openWebAgentChannelForAgent(currentOpenAgent.value);
    return;
  }
  if (channelId === 'Web Voice Agent') {
    openWebVoiceAgentChannelForAgent(currentOpenAgent.value);
    return;
  }
  const bindings = getCachedOpenAgentBindings(currentOpenAgent.value);
  if (channelId === 'Whatsapp' || channelId === 'Whatsapp Voice') {
    openWhatsappAgentNumber(bindings.whatsappDisplayNumber);
    return;
  }
  if (channelId === 'Phone') {
    openPhoneAgentNumber(bindings.phoneDisplayNumber);
  }
}

async function openAgentForRecord(agent) {
  if (agent === null || agent === undefined) {
    return;
  }
  const selectedChannels = getConfiguredDeploymentChannels(agent);
  if (selectedChannels.length === 1 && selectedChannels[0] === 'Web') {
    openWebAgentChannelForAgent(agent);
    return;
  }
  const bindings = await ensureOpenAgentBindings(agent);
  const channels = buildOpenAgentChannels({
    selectedChannels,
    whatsappNumber: bindings.whatsappDisplayNumber,
    phoneNumber: bindings.phoneDisplayNumber,
    t
  });
  if (channels.length === 0) {
    if (selectedChannels.includes('Web') === true) {
      openWebAgentChannelForAgent(agent);
      return;
    }
    if (selectedChannels.includes('Web Voice Agent') === true) {
      openWebVoiceAgentChannelForAgent(agent);
      return;
    }
    toast.error(t('aiChatAgentSetup.openAgent.noAvailableChannels', 'No openable deployment channel is connected for this agent.'));
    return;
  }
  if (shouldOpenSingleAgentChannelDirectly(channels) === true && channels[0].id !== 'Web Voice Agent') {
    currentOpenAgent.value = agent;
    openSelectedAgentChannel(channels[0].id);
    return;
  }
  currentOpenAgent.value = agent;
  openAgentModalChannels.value = channels;
  showOpenAgentModal.value = true;
}

const getAgentEditorRoute = (agent, safeAgentId) => {
  const setupType = typeof agent?.agentdata?.setupType === 'string' ? agent.agentdata.setupType : '';
  if (setupType === 'ivr') {
    return `/aiPhoneIvrSetup?edit=${safeAgentId}`;
  }
  let isVoiceAgent = false;
  if (agent.agentdata !== undefined && agent.agentdata !== null && agent.agentdata.configurations !== undefined && agent.agentdata.configurations !== null && agent.agentdata.configurations.voiceAgent === true) {
    isVoiceAgent = true;
  }
  if (isVoiceAgent === true) {
    return `/voiceAgentSetup?edit=${safeAgentId}`;
  }
  return `/aiChatAgentSetup?edit=${safeAgentId}`;
};

const handleOpenChat = async (rowNumber) => {
  const agent = getAgentByRowNumber(rowNumber);
  if (agent === null) {
    return;
  }
  if (isAgentEditorRoleActive() === true) {
    const agentId = agent.agentid;
    const safeAgentId = encodeURIComponent(String(agentId));
    router.push(getAgentEditorRoute(agent, safeAgentId));
    return;
  }
  await openAgentForRecord(agent);
};

const selectCreateMode = async (mode) => {
  isCreateOptionsModalOpen.value = false;
  isCreating.value = true;
  try {
    const response = await axios.get(`${apiEndpoint}/api/agents/status`);
    const { currentAgents, maxAgents, unlimited } = response.data;
    if (unlimited !== true && currentAgents >= maxAgents) {
      isLimitReachedModalOpen.value = true;
    } else {
      if (isAgentEditorRoleActive() === true && mode !== 'light' && mode !== 'automatic' && mode !== 'ivr') {
        router.push('/aiChatAgentSetup');
        return;
      }
      if (mode === 'light') {
        router.push('/aiChatAgentSetupLight');
      } else if (mode === 'ivr') {
        router.push('/aiPhoneIvrSetup');
      } else if (mode === 'automatic') {
        router.push('/agent/create');
      } else {
        router.push('/aiChatAgentSetup');
      }
    }
  } catch (error) {
    console.error('Error checking agent status:', error);
    handleAuthError(error);
  } finally {
    isCreating.value = false;
  }
};

const handleEditClick = async (rowNumber) => {
  if (rowNumber !== null) {
    const index = rowNumber - 1;
    if (index >= 0 && index < aiAgents.value.length) {
      isEditing.value = rowNumber;
      try {
        const agent = aiAgents.value[index];
        const agentId = agent.agentid;
        const safeAgentId = encodeURIComponent(String(agentId));
        await router.push(getAgentEditorRoute(agent, safeAgentId));
      } finally {
        isEditing.value = null;
      }
    }
  }
};

const openKnowledgeStatusModal = async (rowNumber) => {
  if (rowNumber !== null) {
    const index = rowNumber - 1;
    if (index >= 0 && index < aiAgents.value.length) {
      currentAgentForStatus.value = { ...aiAgents.value[index] };
      knowledgeStatusData.value = [];
      currentStatusPage.value = 1;
      isKnowledgeStatusModalOpen.value = true;
      await fetchKnowledgeStatus(currentAgentForStatus.value.agentid);
    }
  }
};

function normalizeKnowledgeSearchResults(resultsInput) {
  if (Array.isArray(resultsInput) === false) {
    return [];
  }
  const mapped = [];
  for (let i = 0; i < resultsInput.length; i = i + 1) {
    const item = resultsInput[i];
    if (item === null || item === undefined) {
      continue;
    }
    const sourceRaw = typeof item.source === 'string' ? item.source.trim() : '';
    const snippetRaw = typeof item.snippet === 'string' ? item.snippet.trim() : '';
    if (snippetRaw.length === 0) {
      continue;
    }
    mapped.push({
      source: sourceRaw.length > 0 ? sourceRaw : t('aiChats.knowledgeSearchModal.unknownSource', 'Unknown source'),
      snippet: snippetRaw
    });
  }
  return mapped;
}

function openKnowledgeSearchModal() {
  if (currentAgentForStatus.value === null || currentAgentForStatus.value === undefined) {
    return;
  }
  knowledgeSearchError.value = '';
  knowledgeSearchLocationInput.value = '';
  knowledgeSearchResults.value = [];
  isKnowledgeSearchModalOpen.value = true;
}

function closeKnowledgeSearchModal() {
  isKnowledgeSearchModalOpen.value = false;
  isSearchingKnowledgeByLocation.value = false;
}

async function runKnowledgeLocationSearch() {
  if (currentAgentForStatus.value === null || currentAgentForStatus.value === undefined) {
    return;
  }
  const agentId = currentAgentForStatus.value.agentid;
  if (agentId === null || agentId === undefined) {
    return;
  }
  const locationValue = sanitizeTextValue(knowledgeSearchLocationInput.value, modalFilterMaxLength);
  knowledgeSearchLocationInput.value = locationValue;
  knowledgeSearchError.value = '';
  if (locationValue.length === 0) {
    knowledgeSearchResults.value = [];
    knowledgeSearchError.value = t('aiChats.knowledgeSearchModal.locationRequired', 'Please enter a location.');
    return;
  }
  isSearchingKnowledgeByLocation.value = true;
  try {
    const safeAgentId = encodeURIComponent(String(agentId));
    const response = await axios.post(
      `${apiEndpoint}/api/agents/${safeAgentId}/search/location`,
      { location: locationValue, topK: 10 },
      { headers: getAuthHeaders() }
    );
    const payload = response !== null && response !== undefined ? response.data : null;
    const results = payload !== null && payload !== undefined ? payload.results : [];
    knowledgeSearchResults.value = normalizeKnowledgeSearchResults(results);
    if (knowledgeSearchResults.value.length === 0) {
      knowledgeSearchError.value = t('aiChats.knowledgeSearchModal.noMatches', 'No matches were found for this location.');
    }
  } catch (error) {
    console.error('Error searching knowledge base by location:', error);
    handleAuthError(error);
    knowledgeSearchResults.value = [];
    knowledgeSearchError.value = t('aiChats.knowledgeSearchModal.searchFailed', 'Search failed. Please try again.');
  } finally {
    isSearchingKnowledgeByLocation.value = false;
  }
}

const fetchKnowledgeStatus = async (agentId) => {
  if (agentId === null || agentId === undefined) return;
  isLoadingKnowledgeStatus.value = true;
  try {
    const safeAgentId = encodeURIComponent(String(agentId));
    const response = await axios.get(`${apiEndpoint}/api/agents/${safeAgentId}/knowledgeStatus`, {
      headers: getAuthHeaders()
    });
    knowledgeStatusData.value = response.data.filter(item => item.type !== 'fineTuneAnswer').map(item => {
      if (item.type === 'questionAnswer') {
        return { ...item, status: 'Completed', showReport: false };
      }
      const isWebsiteUrlWithZeroProgress =
        item.type === 'websiteurl' &&
        item.progress !== undefined && item.progress !== null &&
        item.progress.current === 0 &&
        item.progress.total === 0;

      if (isWebsiteUrlWithZeroProgress === true) {
        if (item.status !== 'Error' && item.status !== 'Failed') {
          return { ...item, status: 'Processed', showReport: false };
        }
      }
      return { ...item, showReport: false };
    });
    currentStatusPage.value = 1;
  } catch (error) {
    console.error('Error fetching knowledge status:', error);
    knowledgeStatusData.value = [{ name: 'Error', type: 'System', status: 'Error', details: 'Failed to load status.' }];

  } finally {
    isLoadingKnowledgeStatus.value = false;
  }
};

function loadStyle(cssString) {
  const style = document.createElement('style');
  style.type = 'text/css';

  if (style.styleSheet !== null && style.styleSheet !== undefined) {
    style.styleSheet.cssText = cssString;
  } else {
    style.appendChild(document.createTextNode(cssString));
  }

  document.head.appendChild(style);
}

function openScrapeReport(item) {
  const safeName = sanitizeTextValue(item !== undefined && item !== null ? item.name : '', modalFilterMaxLength);
  scrapeReportSource.value = { name: safeName, url: safeName };
  showScrapeReportModal.value = true;
}
function closeScrapeReportModal() {
  showScrapeReportModal.value = false;
  scrapeReportSource.value = null;
}

async function continueWebsiteScan(item) {
  try {
    if (currentAgentForStatus.value === null) return;
    const agentId = currentAgentForStatus.value.agentid;
    const sourceUrl = sanitizeUrlValue(item !== undefined && item !== null ? item.name : '');
    if (sourceUrl === '') {
      return;
    }
    const safeAgentId = encodeURIComponent(String(agentId));
    await axios.post(`${apiEndpoint}/api/agents/${safeAgentId}/scrape/continue`, { rootUrl: sourceUrl, maxPages: 0 }, { headers: getAuthHeaders() });
    await fetchKnowledgeStatus(agentId);
  } catch (e) {
    console.error('Error continuing website scan:', e);
  }
}

async function retryFailedWebsiteScan(item) {
  try {
    if (currentAgentForStatus.value === null) return;
    const agentId = currentAgentForStatus.value.agentid;
    const sourceUrl = sanitizeUrlValue(item !== undefined && item !== null ? item.name : '');
    if (sourceUrl === '') {
      return;
    }
    const safeAgentId = encodeURIComponent(String(agentId));
    await axios.post(`${apiEndpoint}/api/agents/${safeAgentId}/scrape/retryFailed`, { rootUrl: sourceUrl, maxPages: 0 }, { headers: getAuthHeaders() });
    await fetchKnowledgeStatus(agentId);
  } catch (e) {
    console.error('Error retrying failed website scan:', e);
  }
}
</script>
